import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Rnd } from 'react-rnd';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  Plus, Trash2, Image as ImageIcon, Video, Type, Save, 
  FolderPlus, ChevronRight, ChevronDown, ChevronLeft, Upload, X, Folder,
  Maximize2, Minimize2, ZoomIn, ZoomOut, Move, Layers,
  Clock, User, Mic, Camera, Film, Play, Pause, MoreVertical,
  Search, Filter, Grid, List, Edit2, Edit3, Copy, Eye, Settings,
  FileVideo, FileImage, Clapperboard, Layout, Scissors, SkipBack, SkipForward,
  GripVertical, Archive, FileText, ExternalLink, Link2, Music, Download, File
} from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';
import { useDemoMode } from '../context/DemoContext';
import VideoVault from '../components/VideoVault';
import VideoTrimEditor from '../components/VideoTrimEditor';

const API_URL = window.location.origin;

// Shot type options for storyboard
const SHOT_TYPES = [
  'Wide Shot', 'Medium Shot', 'Close-up', 'Extreme Close-up',
  'Over the Shoulder', 'POV', 'Two Shot', 'Establishing Shot',
  'Insert Shot', 'Cutaway', 'Tracking Shot', 'Dolly Shot',
  'Pan', 'Tilt', 'Crane Shot', 'Aerial Shot'
];

// ============ FULLSCREEN VIDEO/IMAGE VIEWER COMPONENT ============
const FullscreenMediaViewer = ({ shot, shots, currentIndex, onClose, onNavigate }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        onNavigate('prev');
      } else if (e.key === 'ArrowRight') {
        onNavigate('next');
      } else if (e.key === ' ') {
        e.preventDefault();
        togglePlayPause();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNavigate]);

  // Auto-play video when shot changes
  useEffect(() => {
    if (videoRef.current && shot?.video_url) {
      videoRef.current.currentTime = shot.trim_start || 0;
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [shot]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const hasVideo = shot?.video_url && shot?.media_type === 'video';
  const hasImage = shot?.thumbnail_url;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < shots.length - 1;

  return (
    <div 
      className="fixed inset-0 bg-black z-50 flex items-center justify-center"
      data-testid="fullscreen-media-viewer"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        data-testid="fullscreen-close-btn"
      >
        <X size={24} className="text-white" />
      </button>

      {/* Shot info overlay */}
      <div className="absolute top-4 left-4 z-50 bg-black/60 rounded-lg px-4 py-2">
        <p className="text-white font-semibold">
          Scene {shot?.scene_number || 1}: Shot {shot?.shot_number || 1}
        </p>
        <p className="text-white/60 text-sm">{shot?.shot_type || 'Shot'}</p>
        <p className="text-white/40 text-xs mt-1">{currentIndex + 1} of {shots.length}</p>
      </div>

      {/* Previous arrow */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onNavigate('prev');
        }}
        disabled={!hasPrev}
        className={`absolute left-64 top-1/2 -translate-y-1/2 z-50 p-4 rounded-full transition-all ${
          hasPrev 
            ? 'bg-white/10 hover:bg-white/30 cursor-pointer' 
            : 'bg-white/5 cursor-not-allowed opacity-30'
        }`}
        data-testid="fullscreen-prev-btn"
      >
        <ChevronLeft size={40} className="text-white" />
      </button>

      {/* Next arrow */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onNavigate('next');
        }}
        disabled={!hasNext}
        className={`absolute right-4 top-1/2 -translate-y-1/2 z-50 p-4 rounded-full transition-all ${
          hasNext 
            ? 'bg-white/10 hover:bg-white/30 cursor-pointer' 
            : 'bg-white/5 cursor-not-allowed opacity-30'
        }`}
        data-testid="fullscreen-next-btn"
      >
        <ChevronRight size={40} className="text-white" />
      </button>

      {/* Media content */}
      <div 
        className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {hasVideo ? (
          <div className="relative">
            <video
              ref={videoRef}
              src={shot.video_url}
              className="max-w-[90vw] max-h-[85vh] rounded-lg"
              controls
              autoPlay
              playsInline
              onClick={(e) => e.stopPropagation()}
            />
            {/* Play/Pause overlay for touch devices */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                togglePlayPause();
              }}
              className="absolute bottom-20 left-1/2 -translate-x-1/2 p-4 bg-black/50 rounded-full hover:bg-black/70 transition-colors md:hidden"
            >
              {isPlaying ? (
                <Pause size={32} className="text-white" fill="white" />
              ) : (
                <Play size={32} className="text-white ml-1" fill="white" />
              )}
            </button>
          </div>
        ) : hasImage ? (
          <img
            src={shot.thumbnail_url}
            alt={`Scene ${shot.scene_number} Shot ${shot.shot_number}`}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
          />
        ) : (
          <div className="w-96 h-64 flex items-center justify-center bg-white/5 rounded-xl">
            <div className="text-center">
              <FileVideo size={64} className="text-white/20 mx-auto mb-4" />
              <p className="text-white/40">No media available</p>
            </div>
          </div>
        )}
      </div>

      {/* Keyboard hints */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 text-white/40 text-sm">
        <span>← → Navigate</span>
        <span>•</span>
        <span>Space Play/Pause</span>
        <span>•</span>
        <span>Esc Close</span>
      </div>
    </div>
  );
};

// ============ MOODBOARD CANVAS COMPONENT ============
const MoodboardCanvas = ({ project, elements, setElements, onSave }) => {
  const [selectedElement, setSelectedElement] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [isDraggingCanvas, setIsDraggingCanvas] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const userId = useUserId();

  // Handle element drag/resize
  const handleElementUpdate = useCallback((id, updates) => {
    setElements(prev => prev.map(el => 
      el.id === id ? { ...el, ...updates } : el
    ));
  }, [setElements]);

  // Add new element
  const addElement = async (type, data = {}) => {
    const newElement = {
      id: `temp-${Date.now()}`,
      project_id: project.id,
      user_id: userId,
      type,
      media_url: data.media_url || null,
      content: data.content || '',
      x: 100 + Math.random() * 200,
      y: 100 + Math.random() * 200,
      width: type === 'text' ? 200 : 250,
      height: type === 'text' ? 100 : 200,
      z_index: elements.length + 1,
      rotation: 0,
      opacity: 1,
      border_radius: 8
    };

    try {
      const response = await fetch(`${API_URL}/api/moodboard/elements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newElement)
      });
      
      if (response.ok) {
        const savedElement = await response.json();
        setElements(prev => [...prev, savedElement]);
        setSelectedElement(savedElement.id);
        toast.success('Element added');
      }
    } catch (error) {
      console.error('Failed to add element:', error);
      toast.error('Failed to add element');
    }
  };

  // Delete element
  const deleteElement = async (id) => {
    try {
      await fetch(`${API_URL}/api/moodboard/elements/${id}`, {
        method: 'DELETE'
      });
      setElements(prev => prev.filter(el => el.id !== id));
      setSelectedElement(null);
      toast.success('Element deleted');
    } catch (error) {
      console.error('Failed to delete element:', error);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);
      
      try {
        toast.loading(`Uploading ${file.name}...`, { id: 'upload' });
        const response = await fetch(`${API_URL}/api/media-storage/upload-simple`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          const isVideo = file.type.startsWith('video/') || data.is_video;
          await addElement(isVideo ? 'video' : 'image', { media_url: data.url });
          toast.dismiss('upload');
          toast.success(`Uploaded ${file.name}`);
        } else {
          toast.dismiss('upload');
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        toast.dismiss('upload');
        console.error('Upload failed:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setShowUploadModal(false);
  };

  // Bring element to front
  const bringToFront = (id) => {
    const maxZ = Math.max(...elements.map(el => el.z_index || 1));
    handleElementUpdate(id, { z_index: maxZ + 1 });
  };

  // Save element position to backend
  const saveElementPosition = async (id) => {
    const element = elements.find(el => el.id === id);
    if (!element) return;
    
    try {
      await fetch(`${API_URL}/api/moodboard/elements/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          x: element.x,
          y: element.y,
          width: element.width,
          height: element.height,
          z_index: element.z_index
        })
      });
    } catch (error) {
      console.error('Failed to save element position:', error);
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/20 bg-black/50">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            data-testid="add-media-btn"
          >
            <Upload size={16} />
            Add Media
          </button>
          <button
            onClick={() => addElement('text', { content: 'Double click to edit' })}
            className="flex items-center gap-2 px-3 py-2 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors text-sm"
            data-testid="add-text-btn"
          >
            <Type size={16} />
            Add Text
          </button>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-white/10 rounded-lg px-2 py-1">
            <button 
              onClick={() => setZoom(z => Math.max(0.25, z - 0.1))}
              className="p-1 hover:bg-white/20 rounded"
            >
              <ZoomOut size={16} className="text-white" />
            </button>
            <span className="text-white text-sm w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button 
              onClick={() => setZoom(z => Math.min(2, z + 0.1))}
              className="p-1 hover:bg-white/20 rounded"
            >
              <ZoomIn size={16} className="text-white" />
            </button>
          </div>
          
          {selectedElement && (
            <button
              onClick={() => deleteElement(selectedElement)}
              className="flex items-center gap-2 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
              data-testid="delete-element-btn"
            >
              <Trash2 size={16} />
              Delete
            </button>
          )}
        </div>
      </div>
      
      {/* Canvas */}
      <div 
        ref={canvasRef}
        className="flex-1 relative overflow-auto bg-black"
        style={{ cursor: isDraggingCanvas ? 'grabbing' : 'default' }}
        onClick={() => setSelectedElement(null)}
      >
        <div 
          className="absolute min-w-[2000px] min-h-[1500px]"
          style={{ 
            transform: `scale(${zoom})`,
            transformOrigin: 'top left'
          }}
        >
          {/* Grid background */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '50px 50px'
            }}
          />
          
          {/* Elements */}
          {elements.map((element) => (
            <Rnd
              key={element.id}
              position={{ x: element.x || 0, y: element.y || 0 }}
              size={{ width: element.width || 200, height: element.height || 200 }}
              onDragStop={(e, d) => {
                handleElementUpdate(element.id, { x: d.x, y: d.y });
                saveElementPosition(element.id);
              }}
              onResizeStop={(e, direction, ref, delta, position) => {
                handleElementUpdate(element.id, {
                  width: parseInt(ref.style.width),
                  height: parseInt(ref.style.height),
                  x: position.x,
                  y: position.y
                });
                saveElementPosition(element.id);
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedElement(element.id);
                bringToFront(element.id);
              }}
              style={{ 
                zIndex: element.z_index || 1,
                opacity: element.opacity || 1
              }}
              bounds="parent"
              minWidth={50}
              minHeight={50}
              className={`group ${selectedElement === element.id ? 'ring-2 ring-white' : ''}`}
            >
              <div 
                className="w-full h-full rounded-lg overflow-hidden border border-white/60 bg-black hover:border-white transition-all cursor-move"
                style={{ 
                  borderRadius: element.border_radius || 8,
                  boxShadow: selectedElement === element.id ? '0 0 20px rgba(255,255,255,0.3)' : '0 4px 20px rgba(0,0,0,0.5)'
                }}
              >
                {element.type === 'image' && element.media_url && (
                  <img 
                    src={element.media_url} 
                    alt="" 
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                )}
                
                {element.type === 'video' && element.media_url && (
                  <video 
                    src={element.media_url}
                    className="w-full h-full object-cover"
                    muted
                    loop
                    onMouseEnter={(e) => e.target.play()}
                    onMouseLeave={(e) => e.target.pause()}
                  />
                )}
                
                {element.type === 'text' && (
                  <div 
                    className="w-full h-full p-4 text-white flex items-center justify-center text-center"
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => handleElementUpdate(element.id, { content: e.target.innerText })}
                  >
                    {element.content || 'Double click to edit'}
                  </div>
                )}
              </div>
              
              {/* Resize handles visible on hover/select */}
              {selectedElement === element.id && (
                <div className="absolute -top-6 left-0 flex items-center gap-1 bg-black/80 rounded px-2 py-1">
                  <Layers size={12} className="text-white/60" />
                  <span className="text-white/60 text-xs">{element.z_index || 1}</span>
                </div>
              )}
            </Rnd>
          ))}
          
          {/* Empty state */}
          {elements.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full border-2 border-dashed border-white/30 flex items-center justify-center">
                  <ImageIcon size={32} className="text-white/30" />
                </div>
                <p className="text-white/50 text-lg mb-2">Your moodboard is empty</p>
                <p className="text-white/30 text-sm">Click "Add Media" to upload images or videos</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowUploadModal(false)}>
          <div 
            className="bg-black border border-white/30 rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-lg font-semibold">Add Media</h3>
              <button onClick={() => setShowUploadModal(false)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div 
              className="border-2 border-dashed border-white/30 rounded-xl p-8 text-center hover:border-white/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={40} className="mx-auto mb-4 text-white/50" />
              <p className="text-white mb-2">Drag and drop or click to upload</p>
              <p className="text-white/50 text-sm">Supports images and videos</p>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        </div>
      )}
    </div>
  );
};


// ============ STORYBOARD COMPONENT ============
const StoryboardView = ({ project, shots, setShots, vaultSelections = [], clearVaultSelections, selectedFolder, folders = [], onFolderUpdate }) => {
  const [selectedShot, setSelectedShot] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingShot, setEditingShot] = useState(null);
  const [showVaultPickerBanner, setShowVaultPickerBanner] = useState(false);
  const [showFolderEditModal, setShowFolderEditModal] = useState(false);
  const [fullscreenShotIndex, setFullscreenShotIndex] = useState(null); // For fullscreen media viewer
  const [folderDetailsCollapsed, setFolderDetailsCollapsed] = useState(false); // Collapse folder details
  const [folderEditForm, setFolderEditForm] = useState({
    name: '',
    description: '',
    client: '',
    shoot_date: '',
    location: '',
    director: '',
    notes: '',
    documents: [],
    music_files: [],
    cover_image: null
  });
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null); // Track which music file is playing
  const audioRef = useRef(null);
  const userId = useUserId();
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const bulkUploadRef = useRef(null);
  const documentInputRef = useRef(null);
  const musicInputRef = useRef(null);
  const coverImageInputRef = useRef(null);

  // Helper function to format seconds as MM:SS
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to get video duration from a video URL
  const getVideoDuration = (videoUrl) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        resolve(video.duration || 0);
      };
      video.onerror = () => {
        resolve(0);
      };
      // Set a timeout in case metadata never loads
      setTimeout(() => resolve(0), 5000);
      video.src = videoUrl;
    });
  };

  // Open folder edit modal with current folder data
  const openFolderEdit = () => {
    const folder = folders.find(f => f.id === selectedFolder);
    if (folder) {
      setFolderEditForm({
        name: folder.name || '',
        description: folder.description || '',
        client: folder.client || '',
        shoot_date: folder.shoot_date || '',
        location: folder.location || '',
        director: folder.director || '',
        notes: folder.notes || '',
        documents: folder.documents || [],
        music_files: folder.music_files || [],
        cover_image: folder.cover_image || null
      });
      setShowFolderEditModal(true);
    }
  };

  // Handle cover image upload
  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', userId);
    
    try {
      toast.loading('Uploading cover image...', { id: 'cover-upload' });
      const response = await fetch(`${API_URL}/api/media-storage/upload-simple`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setFolderEditForm(f => ({ ...f, cover_image: data.url }));
        toast.dismiss('cover-upload');
        toast.success('Cover image uploaded');
      } else {
        toast.dismiss('cover-upload');
        toast.error('Failed to upload cover image');
      }
    } catch (error) {
      toast.dismiss('cover-upload');
      console.error('Cover image upload failed:', error);
      toast.error('Failed to upload cover image');
    }
    
    if (coverImageInputRef.current) {
      coverImageInputRef.current.value = '';
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    toast.loading('Uploading documents...', { id: 'doc-upload' });
    
    const uploadedDocs = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);
      
      try {
        const response = await fetch(`${API_URL}/api/media-storage/upload-simple`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          uploadedDocs.push({
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            name: file.name,
            url: data.url,
            type: file.type,
            size: file.size
          });
        }
      } catch (error) {
        console.error('Document upload failed:', error);
      }
    }
    
    toast.dismiss('doc-upload');
    
    if (uploadedDocs.length > 0) {
      setFolderEditForm(f => ({
        ...f,
        documents: [...(f.documents || []), ...uploadedDocs]
      }));
      toast.success(`Uploaded ${uploadedDocs.length} document(s)`);
    }
    
    // Reset file input
    if (documentInputRef.current) {
      documentInputRef.current.value = '';
    }
  };

  // Handle music file upload
  const handleMusicUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    toast.loading('Uploading music files...', { id: 'music-upload' });
    
    const uploadedMusic = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);
      
      try {
        const response = await fetch(`${API_URL}/api/media-storage/upload-simple`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          uploadedMusic.push({
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            name: file.name,
            url: data.url,
            type: file.type,
            size: file.size
          });
        }
      } catch (error) {
        console.error('Music upload failed:', error);
      }
    }
    
    toast.dismiss('music-upload');
    
    if (uploadedMusic.length > 0) {
      setFolderEditForm(f => ({
        ...f,
        music_files: [...(f.music_files || []), ...uploadedMusic]
      }));
      toast.success(`Uploaded ${uploadedMusic.length} music file(s)`);
    }
    
    // Reset file input
    if (musicInputRef.current) {
      musicInputRef.current.value = '';
    }
  };

  // Remove document
  const removeDocument = (docId) => {
    setFolderEditForm(f => ({
      ...f,
      documents: (f.documents || []).filter(d => d.id !== docId)
    }));
  };

  // Remove music file
  const removeMusicFile = (musicId) => {
    if (currentlyPlaying === musicId) {
      setCurrentlyPlaying(null);
      if (audioRef.current) {
        audioRef.current.pause();
      }
    }
    setFolderEditForm(f => ({
      ...f,
      music_files: (f.music_files || []).filter(m => m.id !== musicId)
    }));
  };

  // Play/pause music
  const toggleMusicPlayback = (music) => {
    if (currentlyPlaying === music.id) {
      // Pause current
      setCurrentlyPlaying(null);
      if (audioRef.current) {
        audioRef.current.pause();
      }
    } else {
      // Play new
      setCurrentlyPlaying(music.id);
      if (audioRef.current) {
        audioRef.current.src = music.url;
        audioRef.current.play().catch(err => {
          console.error('Playback failed:', err);
          toast.error('Failed to play audio');
        });
      }
    }
  };

  // Share folder link
  const shareFolderLink = async (folderId) => {
    try {
      toast.loading('Generating share link...', { id: 'share-folder' });
      
      const response = await fetch(`${API_URL}/api/moodboard/folders/${folderId}/share`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        const shareUrl = `${window.location.origin}/share/storyboard/${data.share_token}`;
        
        // Copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        
        toast.dismiss('share-folder');
        toast.success('Share link copied to clipboard!');
      } else {
        toast.dismiss('share-folder');
        toast.error('Failed to generate share link');
      }
    } catch (error) {
      console.error('Failed to share folder:', error);
      toast.dismiss('share-folder');
      toast.error('Failed to generate share link');
    }
  };

  // Save folder details
  const saveFolderDetails = async () => {
    if (!selectedFolder) return;
    
    try {
      const response = await fetch(`${API_URL}/api/moodboard/folders/${selectedFolder}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(folderEditForm)
      });
      
      if (response.ok) {
        const updated = await response.json();
        toast.success('Folder details saved');
        setShowFolderEditModal(false);
        // Notify parent to refresh folders
        if (onFolderUpdate) onFolderUpdate(updated);
      } else {
        toast.error('Failed to save folder details');
      }
    } catch (error) {
      console.error('Failed to save folder:', error);
      toast.error('Failed to save folder details');
    }
  };

  // Auto-add shots from vault selections when they arrive
  useEffect(() => {
    if (vaultSelections && vaultSelections.length > 0) {
      // Auto-add immediately (no banner waiting)
      addShotsFromVault();
    }
  }, [vaultSelections]);

  // Add shots from vault selections
  const addShotsFromVault = async () => {
    if (!vaultSelections || vaultSelections.length === 0) return;
    
    toast.loading(`Adding ${vaultSelections.length} items from vault...`, { id: 'vault-add' });
    
    let addedCount = 0;
    const newlyAdded = [];
    for (const item of vaultSelections) {
      const mediaData = {
        thumbnail_url: item.media_type === 'image' ? item.media_url : null,
        video_url: item.media_type === 'video' ? item.media_url : null,
        media_type: item.media_type
      };
      
      // Get video duration if it's a video
      let durationEnd = '00:08';
      if (item.media_type === 'video' && item.media_url) {
        const duration = await getVideoDuration(item.media_url);
        durationEnd = formatDuration(duration);
      }
      
      try {
        const lastShot = shots[shots.length - 1 + addedCount];
        const newShot = {
          project_id: project.id,
          user_id: userId,
          folder_id: item.targetFolderId || null,
          scene_number: lastShot?.scene_number || 1,
          shot_number: (lastShot?.shot_number || 0) + 1 + addedCount,
          thumbnail_url: mediaData.thumbnail_url,
          video_url: mediaData.video_url,
          media_type: mediaData.media_type,
          trim_start: 0,
          trim_end: null,
          duration_start: '00:00',
          duration_end: durationEnd,
          character_name: '',
          dialogue: '',
          camera_movement: '',
          shot_type: 'Medium Shot',
          lens: '',
          lighting_notes: '',
          audio_notes: '',
          order: shots.length + 1 + addedCount
        };

        const response = await fetch(`${API_URL}/api/moodboard/shots`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newShot)
        });
        
        if (response.ok) {
          const savedShot = await response.json();
          newlyAdded.push(savedShot);
          addedCount++;
        }
      } catch (error) {
        console.error('Failed to add shot from vault:', error);
      }
    }
    
    if (newlyAdded.length > 0) {
      setShots(prev => [...prev, ...newlyAdded]);
    }
    
    toast.dismiss('vault-add');
    if (addedCount > 0) {
      // Include folder name in success message if available
      const targetFolder = vaultSelections[0]?.targetFolderId 
        ? folders.find(f => f.id === vaultSelections[0].targetFolderId) 
        : null;
      const destination = targetFolder 
        ? `${project.name} / ${targetFolder.name}`
        : project.name;
      toast.success(`Added ${addedCount} shot(s) to ${destination}`);
    } else {
      toast.error('Failed to add shots');
    }
    setShowVaultPickerBanner(false);
    if (clearVaultSelections) clearVaultSelections();
  };

  // Add new shot
  const addShot = async (mediaData = {}) => {
    const lastShot = shots[shots.length - 1];
    
    // Use duration from mediaData if provided (already calculated), otherwise default
    const durationEnd = mediaData.duration_end || '00:08';
    
    const newShot = {
      project_id: project.id,
      user_id: userId,
      folder_id: selectedFolder || null,
      scene_number: lastShot?.scene_number || 1,
      shot_number: (lastShot?.shot_number || 0) + 1,
      thumbnail_url: mediaData.thumbnail_url || null,
      video_url: mediaData.video_url || null,
      media_type: mediaData.media_type || 'image',
      trim_start: 0,
      trim_end: null,
      duration_start: mediaData.duration_start || '00:00',
      duration_end: durationEnd,
      character_name: '',
      dialogue: '',
      camera_movement: '',
      shot_type: 'Medium Shot',
      lens: '',
      lighting_notes: '',
      audio_notes: '',
      order: shots.length + 1
    };

    try {
      const response = await fetch(`${API_URL}/api/moodboard/shots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newShot)
      });
      
      if (response.ok) {
        const savedShot = await response.json();
        setShots(prev => [...prev, savedShot]);
        setEditingShot(savedShot);
        toast.success('Shot added');
      }
    } catch (error) {
      console.error('Failed to add shot:', error);
      toast.error('Failed to add shot');
    }
    
    setShowAddModal(false);
  };

  // Update shot
  const updateShot = async (id, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/moodboard/shots/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        const updatedShot = await response.json();
        setShots(prev => prev.map(s => s.id === id ? updatedShot : s));
        // Also update editing shot if it's the same
        if (editingShot && editingShot.id === id) {
          setEditingShot(updatedShot);
        }
      }
    } catch (error) {
      console.error('Failed to update shot:', error);
    }
  };

  // Delete shot
  const deleteShot = async (id) => {
    try {
      await fetch(`${API_URL}/api/moodboard/shots/${id}`, {
        method: 'DELETE'
      });
      setShots(prev => prev.filter(s => s.id !== id));
      setSelectedShot(null);
      setEditingShot(null);
      toast.success('Shot deleted');
    } catch (error) {
      console.error('Failed to delete shot:', error);
    }
  };

  // Duplicate shot
  const duplicateShot = async (shot) => {
    try {
      toast.loading('Duplicating shot...', { id: 'duplicate' });
      
      const newShot = {
        project_id: shot.project_id || project.id,
        user_id: userId, // Use current userId instead of shot.user_id
        folder_id: shot.folder_id,
        scene_number: shot.scene_number,
        shot_number: (shot.shot_number || 1) + 1,
        thumbnail_url: shot.thumbnail_url,
        video_url: shot.video_url,
        media_type: shot.media_type,
        trim_start: 0, // Reset trim for duplicated shot
        trim_end: null,
        duration_start: shot.duration_start || '00:00',
        duration_end: shot.duration_end || '00:08',
        character_name: shot.character_name || '',
        dialogue: shot.dialogue || '',
        camera_movement: shot.camera_movement || '',
        shot_type: shot.shot_type || 'Medium Shot',
        lens: shot.lens || '',
        lighting_notes: shot.lighting_notes || '',
        audio_notes: shot.audio_notes || '',
        order: shots.length + 1
      };

      const response = await fetch(`${API_URL}/api/moodboard/shots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newShot)
      });
      
      if (response.ok) {
        const savedShot = await response.json();
        setShots(prev => [...prev, savedShot]);
        toast.dismiss('duplicate');
        toast.success('Shot duplicated');
      } else {
        toast.dismiss('duplicate');
        toast.error('Failed to duplicate shot');
      }
    } catch (error) {
      console.error('Failed to duplicate shot:', error);
      toast.dismiss('duplicate');
      toast.error('Failed to duplicate shot');
    }
  };

  // Handle image thumbnail upload
  const handleImageUpload = async (e, shotId = null) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', userId);
    
    try {
      toast.loading('Uploading image...', { id: 'upload' });
      const response = await fetch(`${API_URL}/api/media-storage/upload-simple`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.dismiss('upload');
        if (shotId) {
          updateShot(shotId, { thumbnail_url: data.url, media_type: 'image', video_url: null });
          toast.success('Image uploaded');
        } else {
          addShot({ thumbnail_url: data.url, media_type: 'image' });
        }
      } else {
        toast.dismiss('upload');
        toast.error('Failed to upload image');
      }
    } catch (error) {
      toast.dismiss('upload');
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
    }
  };

  // Handle video upload
  const handleVideoUpload = async (e, shotId = null) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', userId);
    
    try {
      toast.loading('Uploading video...', { id: 'upload' });
      const response = await fetch(`${API_URL}/api/media-storage/upload-simple`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.dismiss('upload');
        
        // Get video duration
        const duration = await getVideoDuration(data.url);
        const durationEnd = formatDuration(duration);
        
        if (shotId) {
          updateShot(shotId, { 
            video_url: data.url, 
            media_type: 'video',
            duration_start: '00:00',
            duration_end: durationEnd
          });
          toast.success('Video uploaded');
        } else {
          addShot({ 
            video_url: data.url, 
            media_type: 'video',
            duration_start: '00:00',
            duration_end: durationEnd
          });
        }
      } else {
        toast.dismiss('upload');
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to upload video');
      }
    } catch (error) {
      toast.dismiss('upload');
      console.error('Upload failed:', error);
      toast.error('Failed to upload video');
    }
  };

  // Handle bulk upload (multiple images/videos)
  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setShowAddModal(false);
    
    const totalFiles = files.length;
    let uploadedCount = 0;
    let failedCount = 0;
    
    toast.loading(`Uploading ${totalFiles} files...`, { id: 'bulk-upload' });
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);
      
      try {
        const response = await fetch(`${API_URL}/api/media-storage/upload-simple`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          const isVideo = file.type.startsWith('video/') || data.is_video;
          
          if (isVideo) {
            // Get video duration
            const duration = await getVideoDuration(data.url);
            const durationEnd = formatDuration(duration);
            await addShot({ 
              video_url: data.url, 
              media_type: 'video',
              duration_start: '00:00',
              duration_end: durationEnd
            });
          } else {
            await addShot({ thumbnail_url: data.url, media_type: 'image' });
          }
          uploadedCount++;
        } else {
          failedCount++;
        }
        
        // Update progress
        toast.loading(`Uploading ${uploadedCount + failedCount}/${totalFiles} files...`, { id: 'bulk-upload' });
      } catch (error) {
        console.error('Upload failed:', error);
        failedCount++;
      }
    }
    
    toast.dismiss('bulk-upload');
    
    if (failedCount === 0) {
      toast.success(`Successfully uploaded ${uploadedCount} files`);
    } else {
      toast.warning(`Uploaded ${uploadedCount} files, ${failedCount} failed`);
    }
  };

  // Handle drag end for reordering shots
  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    
    if (sourceIndex === destIndex) return;
    
    // Reorder the shots array and update order values
    const reorderedShots = Array.from(sortedShots);
    const [movedShot] = reorderedShots.splice(sourceIndex, 1);
    reorderedShots.splice(destIndex, 0, movedShot);
    
    // Update order field on each shot for instant visual update
    const updatedShotsMap = new Map();
    reorderedShots.forEach((shot, index) => {
      updatedShotsMap.set(shot.id, { ...shot, order: index + 1 });
    });
    
    // Update local state immediately for smooth UX
    // Use functional update to ensure we get the latest state
    setShots(prevShots => {
      return prevShots.map(shot => {
        const updated = updatedShotsMap.get(shot.id);
        return updated || shot;
      });
    });
    
    // Update order on backend
    try {
      const shotIds = reorderedShots.map(s => s.id);
      const response = await fetch(`${API_URL}/api/moodboard/shots/reorder?project_id=${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shot_ids: shotIds })
      });
      
      if (!response.ok) {
        throw new Error('API error');
      }
      
      toast.success('Shots reordered');
    } catch (error) {
      console.error('Failed to reorder shots:', error);
      toast.error('Failed to save new order');
      // Revert on error - refetch shots
      const fetchResponse = await fetch(`${API_URL}/api/moodboard/shots?project_id=${project.id}`);
      if (fetchResponse.ok) {
        const freshShots = await fetchResponse.json();
        setShots(freshShots);
      }
    }
  };

  // Filter shots by selected folder (if any), then sort by order for drag-and-drop
  const visibleShots = selectedFolder
    ? shots.filter(s => s.folder_id === selectedFolder)
    : shots;
  const sortedShots = [...visibleShots].sort((a, b) => (a.order || 0) - (b.order || 0));
  
  // Get the current folder name for display
  const currentFolder = selectedFolder ? folders.find(f => f.id === selectedFolder) : null;
  
  // Calculate folder stats (total duration, video count, image count)
  const folderStats = React.useMemo(() => {
    if (!currentFolder) return null;
    
    const folderShots = shots.filter(s => s.folder_id === selectedFolder);
    let totalDuration = 0;
    let videoCount = 0;
    let imageCount = 0;
    
    folderShots.forEach(shot => {
      if (shot.media_type === 'video') {
        videoCount++;
        // Parse duration from "00:00 -> 00:08" format or use duration field
        if (shot.duration) {
          totalDuration += shot.duration;
        } else if (shot.time_range) {
          const match = shot.time_range.match(/(\d+):(\d+)\s*->\s*(\d+):(\d+)/);
          if (match) {
            const startSec = parseInt(match[1]) * 60 + parseInt(match[2]);
            const endSec = parseInt(match[3]) * 60 + parseInt(match[4]);
            totalDuration += (endSec - startSec);
          }
        }
      } else {
        imageCount++;
      }
    });
    
    // Format total duration
    const mins = Math.floor(totalDuration / 60);
    const secs = Math.floor(totalDuration % 60);
    const formattedDuration = `${mins}:${secs.toString().padStart(2, '0')}`;
    
    return {
      totalShots: folderShots.length,
      videoCount,
      imageCount,
      totalDuration: formattedDuration,
      rawDuration: totalDuration
    };
  }, [currentFolder, shots, selectedFolder]);

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Folder Details Header - Shows when a folder is selected */}
      {currentFolder && folderStats && (
        <div className="bg-black border-b border-white/10">
          {/* Collapse Toggle Bar */}
          <div 
            className="px-6 py-3 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
            onClick={() => setFolderDetailsCollapsed(!folderDetailsCollapsed)}
          >
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-blue-500/20 rounded">
                <Folder size={16} className="text-blue-400" />
              </div>
              <h3 className="text-white font-semibold">{currentFolder.name}</h3>
              <span className="text-white/40 text-sm">•</span>
              <span className="text-white/50 text-sm">{folderStats.totalShots} shots</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openFolderEdit();
                }}
                className="p-1.5 text-white/40 hover:text-white hover:bg-white/10 rounded transition-colors"
                title="Edit folder details"
              >
                <Edit3 size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  shareFolderLink(currentFolder.id);
                }}
                className="p-1.5 text-white/40 hover:text-blue-400 hover:bg-white/10 rounded transition-colors"
                title="Share folder"
              >
                <Link2 size={14} />
              </button>
              <div className={`p-1 text-white/40 transition-transform ${folderDetailsCollapsed ? '' : 'rotate-180'}`}>
                <ChevronDown size={18} />
              </div>
            </div>
          </div>
          
          {/* Collapsible Content */}
          {!folderDetailsCollapsed && (
            <div className="px-6 pb-4">
              <div className="flex items-start gap-4">
                {/* Cover Image */}
                {currentFolder.cover_image && (
                  <div className="flex-shrink-0">
                    <img 
                      src={currentFolder.cover_image} 
                      alt={currentFolder.name}
                      className="w-28 h-28 object-cover rounded-lg border border-white/20"
                    />
                  </div>
                )}
                
                <div className="flex-1">
                  {/* Folder Details Row */}
                  <div className="flex flex-wrap items-center gap-4 text-sm mb-2">
                    {currentFolder.client && (
                      <div className="flex items-center gap-1.5 text-white/60">
                        <User size={12} />
                        <span>{currentFolder.client}</span>
                      </div>
                    )}
                    {currentFolder.director && (
                      <div className="flex items-center gap-1.5 text-white/60">
                        <Clapperboard size={12} />
                        <span>{currentFolder.director}</span>
                      </div>
                    )}
                    {currentFolder.shoot_date && (
                      <div className="flex items-center gap-1.5 text-white/60">
                        <Clock size={12} />
                        <span>{currentFolder.shoot_date}</span>
                      </div>
                    )}
                    {currentFolder.location && (
                      <div className="flex items-center gap-1.5 text-white/60">
                        <Camera size={12} />
                        <span>{currentFolder.location}</span>
                      </div>
                    )}
                  </div>
                  
                  {currentFolder.description && (
                    <p className="text-white/60 text-sm mb-1">{currentFolder.description}</p>
                  )}
                  {currentFolder.notes && (
                    <p className="text-white/50 text-xs italic border-l-2 border-white/20 pl-2">{currentFolder.notes}</p>
                  )}
                </div>
                
                {/* Folder Stats */}
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-xl font-bold text-white">{folderStats.totalShots}</div>
                    <div className="text-white/50 text-xs uppercase tracking-wider">Shots</div>
                  </div>
                  {folderStats.videoCount > 0 && (
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-400">{folderStats.videoCount}</div>
                      <div className="text-white/50 text-xs uppercase tracking-wider">Videos</div>
                    </div>
                  )}
                  {folderStats.imageCount > 0 && (
                    <div className="text-center">
                      <div className="text-xl font-bold text-purple-400">{folderStats.imageCount}</div>
                      <div className="text-white/50 text-xs uppercase tracking-wider">Images</div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Documents and Music Section */}
              {((currentFolder.documents && currentFolder.documents.length > 0) || 
                (currentFolder.music_files && currentFolder.music_files.length > 0)) && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex flex-wrap gap-6">
                    {/* Documents */}
                    {currentFolder.documents && currentFolder.documents.length > 0 && (
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText size={16} className="text-blue-400" />
                          <span className="text-white font-medium text-sm">Documents ({currentFolder.documents.length})</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {currentFolder.documents.map((doc) => {
                            const isImage = doc.type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(doc.name);
                            return isImage ? (
                              <a
                                key={doc.id}
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative group"
                                title={doc.name}
                              >
                                <img 
                                  src={doc.url} 
                                  alt={doc.name}
                                  className="w-14 h-14 object-cover rounded-lg border border-white/20 hover:border-blue-400 transition-colors"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 rounded-lg flex items-center justify-center transition-opacity">
                                  <Download size={14} className="text-white" />
                                </div>
                              </a>
                            ) : (
                              <a
                                key={doc.id}
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 text-blue-300 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
                                title={doc.name}
                              >
                                <FileText size={14} />
                                <span className="truncate max-w-[120px]">{doc.name}</span>
                                <Download size={12} />
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Music Files */}
                    {currentFolder.music_files && currentFolder.music_files.length > 0 && (
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 mb-2">
                          <Music size={16} className="text-purple-400" />
                          <span className="text-white font-medium text-sm">Music ({currentFolder.music_files.length})</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {currentFolder.music_files.map((music) => (
                            <button
                              key={music.id}
                              onClick={() => toggleMusicPlayback(music)}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                currentlyPlaying === music.id 
                                  ? 'bg-purple-500 text-white' 
                                  : 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                              }`}
                              title={music.name}
                            >
                              {currentlyPlaying === music.id ? <Pause size={14} /> : <Play size={14} />}
                              <span className="truncate max-w-[120px]">{music.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Folder Edit Modal */}
      {showFolderEditModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowFolderEditModal(false)}>
          <div 
            className="bg-black border border-white/30 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-lg font-semibold">Edit Folder Details</h3>
              <button onClick={() => setShowFolderEditModal(false)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Cover Image */}
              <div>
                <label className="block text-white/60 text-sm mb-2">Cover Image</label>
                <div className="flex items-start gap-4">
                  {folderEditForm.cover_image ? (
                    <div className="relative">
                      <img 
                        src={folderEditForm.cover_image} 
                        alt="Cover" 
                        className="w-32 h-32 object-cover rounded-lg border border-white/20"
                      />
                      <button
                        onClick={() => setFolderEditForm(f => ({ ...f, cover_image: null }))}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => coverImageInputRef.current?.click()}
                      className="w-32 h-32 border border-dashed border-white/30 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-white/50 transition-colors"
                    >
                      <ImageIcon size={24} className="text-white/30 mb-2" />
                      <span className="text-white/40 text-xs">Add Cover</span>
                    </div>
                  )}
                  <input
                    ref={coverImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageUpload}
                    className="hidden"
                  />
                  <div className="flex-1 text-white/40 text-xs">
                    <p>Upload a cover image for this folder.</p>
                    <p className="mt-1">Recommended: Square image, min 400x400px</p>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-white/60 text-sm mb-1">Folder Name</label>
                <input
                  type="text"
                  value={folderEditForm.name}
                  onChange={(e) => setFolderEditForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Enter folder name..."
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:border-white/40 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-white/60 text-sm mb-1">Description</label>
                <textarea
                  value={folderEditForm.description}
                  onChange={(e) => setFolderEditForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description of this folder's content..."
                  rows={2}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:border-white/40 focus:outline-none resize-none"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/60 text-sm mb-1">Client</label>
                  <input
                    type="text"
                    value={folderEditForm.client}
                    onChange={(e) => setFolderEditForm(f => ({ ...f, client: e.target.value }))}
                    placeholder="Client name"
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:border-white/40 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Director</label>
                  <input
                    type="text"
                    value={folderEditForm.director}
                    onChange={(e) => setFolderEditForm(f => ({ ...f, director: e.target.value }))}
                    placeholder="Director name"
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:border-white/40 focus:outline-none"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/60 text-sm mb-1">Shoot Date</label>
                  <input
                    type="text"
                    value={folderEditForm.shoot_date}
                    onChange={(e) => setFolderEditForm(f => ({ ...f, shoot_date: e.target.value }))}
                    placeholder="e.g., April 25, 2026"
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:border-white/40 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Location</label>
                  <input
                    type="text"
                    value={folderEditForm.location}
                    onChange={(e) => setFolderEditForm(f => ({ ...f, location: e.target.value }))}
                    placeholder="Shoot location"
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:border-white/40 focus:outline-none"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-white/60 text-sm mb-1">Notes</label>
                <textarea
                  value={folderEditForm.notes}
                  onChange={(e) => setFolderEditForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Additional notes about this folder..."
                  rows={3}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:border-white/40 focus:outline-none resize-none"
                />
              </div>
              
              {/* Documents Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-white/60 text-sm">Documents</label>
                  <button
                    onClick={() => documentInputRef.current?.click()}
                    className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                  >
                    <Plus size={14} />
                    Add Documents
                  </button>
                </div>
                <input
                  ref={documentInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.csv,image/*,.png,.jpg,.jpeg,.gif,.webp,.svg"
                  onChange={handleDocumentUpload}
                  className="hidden"
                />
                {folderEditForm.documents && folderEditForm.documents.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {folderEditForm.documents.map((doc) => {
                      const isImage = doc.type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(doc.name);
                      return (
                        <div key={doc.id} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {isImage ? (
                              <img src={doc.url} alt={doc.name} className="w-10 h-10 object-cover rounded flex-shrink-0" />
                            ) : (
                              <FileText size={16} className="text-blue-400 flex-shrink-0" />
                            )}
                            <span className="text-white text-sm truncate">{doc.name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 text-white/40 hover:text-white"
                              title="Download"
                            >
                              <Download size={14} />
                            </a>
                            <button
                              onClick={() => removeDocument(doc.id)}
                              className="p-1 text-white/40 hover:text-red-400"
                              title="Remove"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div 
                    onClick={() => documentInputRef.current?.click()}
                    className="border border-dashed border-white/20 rounded-lg p-4 text-center cursor-pointer hover:border-white/40 transition-colors"
                  >
                    <File size={24} className="text-white/30 mx-auto mb-2" />
                    <p className="text-white/40 text-sm">Click to upload files</p>
                    <p className="text-white/20 text-xs mt-1">PDF, DOC, XLS, Images, and more</p>
                  </div>
                )}
              </div>
              
              {/* Music Files Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-white/60 text-sm">Music / Audio</label>
                  <button
                    onClick={() => musicInputRef.current?.click()}
                    className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300"
                  >
                    <Plus size={14} />
                    Add Music
                  </button>
                </div>
                <input
                  ref={musicInputRef}
                  type="file"
                  multiple
                  accept="audio/*,.mp3,.wav,.aac,.flac,.m4a,.ogg"
                  onChange={handleMusicUpload}
                  className="hidden"
                />
                {folderEditForm.music_files && folderEditForm.music_files.length > 0 ? (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {folderEditForm.music_files.map((music) => (
                      <div key={music.id} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <button
                            onClick={() => toggleMusicPlayback(music)}
                            className={`p-1.5 rounded-full ${currentlyPlaying === music.id ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/60 hover:text-white'}`}
                          >
                            {currentlyPlaying === music.id ? <Pause size={12} /> : <Play size={12} className="ml-0.5" />}
                          </button>
                          <Music size={14} className="text-purple-400 flex-shrink-0" />
                          <span className="text-white text-sm truncate">{music.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <a
                            href={music.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 text-white/40 hover:text-white"
                            title="Download"
                          >
                            <Download size={14} />
                          </a>
                          <button
                            onClick={() => removeMusicFile(music.id)}
                            className="p-1 text-white/40 hover:text-red-400"
                            title="Remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div 
                    onClick={() => musicInputRef.current?.click()}
                    className="border border-dashed border-white/20 rounded-lg p-4 text-center cursor-pointer hover:border-white/40 transition-colors"
                  >
                    <Music size={24} className="text-white/30 mx-auto mb-2" />
                    <p className="text-white/40 text-sm">Click to upload music files</p>
                    <p className="text-white/20 text-xs mt-1">MP3, WAV, AAC, FLAC, M4A, OGG</p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowFolderEditModal(false)}
                  className="flex-1 py-2.5 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveFolderDetails}
                  className="flex-1 py-2.5 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  data-testid="save-folder-details"
                >
                  Save Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Vault Picker Banner */}
      {showVaultPickerBanner && vaultSelections && vaultSelections.length > 0 && (
        <div className="px-4 py-3 bg-blue-500/20 border-b border-blue-500/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Archive size={18} className="text-blue-400" />
            <span className="text-white text-sm">
              <strong>{vaultSelections.length}</strong> items selected from vault
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setShowVaultPickerBanner(false);
                if (clearVaultSelections) clearVaultSelections();
              }}
              className="px-3 py-1.5 text-white/70 hover:text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={addShotsFromVault}
              className="px-4 py-1.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600"
            >
              Add to Storyboard
            </button>
          </div>
        </div>
      )}
      
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 px-3 sm:px-4 py-2 sm:py-3 border-b border-white/20 bg-black/50">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors text-xs sm:text-sm font-medium"
            data-testid="add-shot-btn"
          >
            <Plus size={14} className="sm:w-4 sm:h-4" />
            Add Shot
          </button>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="text-white/40 text-[10px] sm:text-xs flex items-center gap-1 hidden sm:flex">
            <GripVertical size={12} className="sm:w-3.5 sm:h-3.5" />
            Drag to reorder
          </div>
          <div className="text-white/60 text-xs sm:text-sm">
            {sortedShots.length} {sortedShots.length === 1 ? 'shot' : 'shots'}
          </div>
        </div>
      </div>
      
      {/* Shots Grid with Drag and Drop */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
          {sortedShots.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full border-2 border-dashed border-white/30 flex items-center justify-center">
                  <Clapperboard size={32} className="text-white/30" />
                </div>
                <p className="text-white/50 text-lg mb-2">No shots yet</p>
                <p className="text-white/30 text-sm">Click "Add Shot" to start your storyboard</p>
              </div>
            </div>
          ) : (
            <Droppable droppableId="shots" type="SHOT" isCombineEnabled={false}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 p-1 transition-all duration-200 ${
                    snapshot.isDraggingOver ? 'bg-blue-500/5 rounded-xl' : ''
                  }`}
                >
                  {sortedShots.map((shot, index) => (
                    <Draggable key={shot.id} draggableId={shot.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`${
                            snapshot.isDragging 
                              ? 'z-[100] opacity-95' 
                              : ''
                          }`}
                          style={{
                            ...provided.draggableProps.style,
                            cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                          }}
                        >
                          <div className={`transition-all duration-200 ${
                            snapshot.isDragging 
                              ? 'scale-105 rotate-1 shadow-2xl shadow-blue-500/40 ring-2 ring-blue-500' 
                              : 'hover:scale-[1.02] hover:shadow-lg'
                          }`}>
                            <ShotCard 
                              shot={shot}
                              isSelected={selectedShot === shot.id}
                              isDragging={snapshot.isDragging}
                              dragHandleProps={{}} 
                              onClick={() => {
                                setSelectedShot(shot.id);
                                setEditingShot(shot);
                              }}
                              onDelete={() => deleteShot(shot.id)}
                              onDuplicate={() => duplicateShot(shot)}
                              onOpenFullscreen={() => setFullscreenShotIndex(index)}
                            />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}
        </div>
      </DragDropContext>
      
      {/* Shot Detail Panel */}
      {editingShot && (
        <ShotDetailPanel 
          shot={editingShot}
          onUpdate={(updates) => {
            updateShot(editingShot.id, updates);
            setEditingShot({ ...editingShot, ...updates });
          }}
          onClose={() => {
            setEditingShot(null);
            setSelectedShot(null);
          }}
          onUploadImage={(e) => handleImageUpload(e, editingShot.id)}
          onUploadVideo={(e) => handleVideoUpload(e, editingShot.id)}
          currentUserId={userId}
        />
      )}
      
      {/* Add Shot Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
          <div 
            className="bg-black border border-white/30 rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-lg font-semibold">Add New Shot</h3>
              <button onClick={() => setShowAddModal(false)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Bulk Upload - Primary option */}
              <button
                onClick={() => bulkUploadRef.current?.click()}
                className="w-full p-4 border-2 border-dashed border-green-500/40 rounded-xl hover:border-green-500/60 hover:bg-green-500/5 transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <Upload size={20} className="text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Bulk Upload</p>
                    <p className="text-white/50 text-sm">Select multiple images or videos at once</p>
                  </div>
                </div>
              </button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-black px-2 text-white/40">or add single shot</span>
                </div>
              </div>
              
              <button
                onClick={() => addShot()}
                className="w-full p-4 border border-white/20 rounded-xl hover:border-white/40 hover:bg-white/5 transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                    <Film size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Blank Shot</p>
                    <p className="text-white/50 text-sm">Add details manually</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full p-4 border border-white/20 rounded-xl hover:border-white/40 hover:bg-white/5 transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                    <FileImage size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Single Image</p>
                    <p className="text-white/50 text-sm">Upload one image thumbnail</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => videoInputRef.current?.click()}
                className="w-full p-4 border border-white/20 rounded-xl hover:border-white/40 hover:bg-white/5 transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <FileVideo size={20} className="text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Single Video</p>
                    <p className="text-white/50 text-sm">Upload one video clip (plays on hover)</p>
                  </div>
                </div>
              </button>
              
              {/* Video URL Input */}
              <div className="border border-white/20 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Video size={20} className="text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Video URL</p>
                    <p className="text-white/50 text-sm">Paste a direct link to a video</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <input
                    type="url"
                    id="video-url-input"
                    placeholder="https://example.com/video.mp4"
                    className="flex-1 bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:border-white/40 focus:outline-none placeholder:text-white/30"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const url = e.target.value.trim();
                        if (url) {
                          addShot({ video_url: url, media_type: 'video' });
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('video-url-input');
                      const url = input?.value?.trim();
                      if (url) {
                        addShot({ video_url: url, media_type: 'video' });
                        input.value = '';
                      } else {
                        toast.error('Please enter a video URL');
                      }
                    }}
                    className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 text-sm font-medium"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
            
            <input
              ref={bulkUploadRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleBulkUpload}
              className="hidden"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e)}
              className="hidden"
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              onChange={(e) => handleVideoUpload(e)}
              className="hidden"
            />
          </div>
        </div>
      )}
      
      {/* Fullscreen Media Viewer */}
      {fullscreenShotIndex !== null && sortedShots[fullscreenShotIndex] && (
        <FullscreenMediaViewer
          shot={sortedShots[fullscreenShotIndex]}
          shots={sortedShots}
          currentIndex={fullscreenShotIndex}
          onClose={() => setFullscreenShotIndex(null)}
          onNavigate={(direction) => {
            if (direction === 'prev' && fullscreenShotIndex > 0) {
              setFullscreenShotIndex(fullscreenShotIndex - 1);
            } else if (direction === 'next' && fullscreenShotIndex < sortedShots.length - 1) {
              setFullscreenShotIndex(fullscreenShotIndex + 1);
            }
          }}
        />
      )}
      
      {/* Global Audio Player for Music Files */}
      <audio ref={audioRef} onEnded={() => setCurrentlyPlaying(null)} className="hidden" />
    </div>
  );
};


// ============ SHOT CARD COMPONENT ============
const ShotCard = ({ shot, isSelected, isDragging, dragHandleProps, onClick, onDelete, onDuplicate, onOpenFullscreen }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [actualDuration, setActualDuration] = useState(null);
  const videoRef = useRef(null);

  // Handle video hover play
  useEffect(() => {
    if (videoRef.current && shot.video_url) {
      if (isHovering && !isDragging) {
        videoRef.current.currentTime = shot.trim_start || 0;
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = shot.trim_start || 0;
      }
    }
  }, [isHovering, isDragging, shot.video_url, shot.trim_start]);

  // Get actual video duration when metadata loads
  useEffect(() => {
    if (videoRef.current && shot.video_url) {
      const handleLoadedMetadata = () => {
        const duration = videoRef.current?.duration || 0;
        if (duration > 0) {
          const mins = Math.floor(duration / 60);
          const secs = Math.floor(duration % 60);
          setActualDuration(`${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
        }
      };
      
      const video = videoRef.current;
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      
      // If already loaded, get duration immediately
      if (video.readyState >= 1 && video.duration > 0) {
        handleLoadedMetadata();
      }
      
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [shot.video_url]);

  // Determine what to show: video or image
  const hasVideo = shot.video_url && shot.media_type === 'video';
  const hasImage = shot.thumbnail_url;
  
  // Use actual duration if available, otherwise fall back to stored duration
  const displayDuration = actualDuration || shot.duration_end || '00:00';
  
  // Handle click on media area to open fullscreen
  const handleMediaClick = (e) => {
    e.stopPropagation();
    if (onOpenFullscreen) {
      onOpenFullscreen();
    }
  };
  
  return (
    <div 
      className={`relative glassmorphic-card transition-all duration-200 group select-none ${
        isSelected ? 'ring-2 ring-white' : ''
      } ${isDragging ? 'ring-2 ring-blue-500 opacity-90' : 'hover:ring-1 hover:ring-white/30'}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        setShowMenu(false);
      }}
      data-testid={`shot-card-${shot.id}`}
    >
      {/* Drag indicator - shows on hover */}
      <div 
        className="absolute top-2 left-2 z-20 p-1.5 bg-black/70 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
      >
        <GripVertical size={16} className="text-white/70" />
      </div>
      
      {/* Thumbnail / Video - Clickable for fullscreen */}
      <div 
        className="aspect-video bg-black/50 relative overflow-hidden cursor-pointer"
        onClick={handleMediaClick}
        data-testid={`shot-media-${shot.id}`}
      >
        {hasVideo ? (
          <>
            {/* Video element - plays on hover */}
            <video 
              ref={videoRef}
              src={shot.video_url}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              preload="metadata"
            />
            {/* Play icon overlay when not hovering */}
            {!isHovering && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Play size={24} className="text-white ml-1" fill="white" />
                </div>
              </div>
            )}
          </>
        ) : hasImage ? (
          <>
            <img 
              src={shot.thumbnail_url} 
              alt={`Scene ${shot.scene_number} Shot ${shot.shot_number}`}
              className="w-full h-full object-cover"
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FileVideo size={40} className="text-white/20" />
          </div>
        )}
        
        {/* Shot type indicator overlay */}
        <div className="absolute top-2 right-2 bg-black/60 rounded px-2 py-1 flex items-center gap-1">
          <Camera size={12} className="text-white/80" />
          <span className="text-white/80 text-xs">{shot.shot_type || 'Shot'}</span>
        </div>
        
        {/* Menu button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className="absolute bottom-2 right-2 p-1.5 bg-black/70 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 hover:bg-black/90"
          data-testid={`shot-menu-btn-${shot.id}`}
        >
          <MoreVertical size={16} className="text-white" />
        </button>
      </div>
      
      {/* Menu dropdown - positioned outside video container */}
      {showMenu && (
        <>
          {/* Backdrop to close menu */}
          <div 
            className="fixed inset-0 z-[100]" 
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(false);
            }}
          />
          {/* Menu */}
          <div 
            className="absolute top-[calc(56.25%+8px)] right-2 bg-black border border-white/30 rounded-lg py-1 z-[101] shadow-2xl min-w-[150px]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClick();
                setShowMenu(false);
              }}
              className="w-full px-4 py-2.5 text-left text-white hover:bg-white/10 text-sm flex items-center gap-2"
            >
              <Edit2 size={14} />
              Edit Details
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onDuplicate) onDuplicate();
                setShowMenu(false);
              }}
              className="w-full px-4 py-2.5 text-left text-white hover:bg-white/10 text-sm flex items-center gap-2"
            >
              <Copy size={14} />
              Duplicate
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
                setShowMenu(false);
              }}
              className="w-full px-4 py-2.5 text-left text-red-400 hover:bg-white/10 text-sm flex items-center gap-2"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        </>
      )}
      
      {/* Info - Clickable to open details panel */}
      <div className="p-4 cursor-pointer hover:bg-white/5 transition-colors" onClick={onClick}>
        <h4 className="text-white font-semibold mb-2">
          SCENE {shot.scene_number}: <span className="font-normal text-white/70">Shot {shot.shot_number}</span>
        </h4>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-white/60">
            <Clock size={14} />
            <span>{shot.duration_start || '00:00'} → {displayDuration}</span>
          </div>
          
          {shot.character_name && (
            <div className="flex items-center gap-2 text-white/60">
              <User size={14} />
              <span>{shot.character_name}</span>
            </div>
          )}
          
          {shot.dialogue && (
            <div className="flex items-start gap-2 text-white/60">
              <Mic size={14} className="mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2 italic">"{shot.dialogue}"</span>
            </div>
          )}
          
          {shot.camera_movement && (
            <div className="flex items-start gap-2 text-white/60">
              <Camera size={14} className="mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2">{shot.camera_movement}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ============ SHOT DETAIL PANEL ============
const ShotDetailPanel = ({ shot, onUpdate, onClose, onUploadImage, onUploadVideo, currentUserId }) => {
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const canvasRef = useRef(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTrimEditor, setShowTrimEditor] = useState(false);
  
  // Get video duration when loaded
  const handleVideoLoaded = () => {
    if (videoPreviewRef.current) {
      const duration = videoPreviewRef.current.duration;
      setVideoDuration(duration);
      if (shot.trim_start) {
        videoPreviewRef.current.currentTime = shot.trim_start;
      }
    }
  };

  // Update current time display
  const handleTimeUpdate = () => {
    if (videoPreviewRef.current) {
      setCurrentTime(videoPreviewRef.current.currentTime);
      // Loop within trim range
      if (shot.trim_end && videoPreviewRef.current.currentTime >= shot.trim_end) {
        videoPreviewRef.current.currentTime = shot.trim_start || 0;
      }
    }
  };

  // Toggle play/pause
  const togglePlay = () => {
    if (videoPreviewRef.current) {
      if (isPlaying) {
        videoPreviewRef.current.pause();
      } else {
        videoPreviewRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Set trim start to current time
  const setTrimStart = () => {
    onUpdate({ trim_start: currentTime });
    toast.success(`Trim start set to ${formatTime(currentTime)}`);
  };

  // Set trim end to current time
  const setTrimEnd = () => {
    onUpdate({ trim_end: currentTime });
    toast.success(`Trim end set to ${formatTime(currentTime)}`);
  };

  // Capture frame as thumbnail
  const captureFrame = () => {
    if (!videoPreviewRef.current || !canvasRef.current) return;
    
    const video = videoPreviewRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      
      const formData = new FormData();
      formData.append('file', blob, 'thumbnail.jpg');
      formData.append('user_id', shot.user_id);
      
      try {
        toast.loading('Saving thumbnail...', { id: 'capture' });
        const response = await fetch(`${API_URL}/api/media-storage/upload-simple`, {
          method: 'POST',
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          onUpdate({ thumbnail_url: data.url });
          toast.dismiss('capture');
          toast.success('Thumbnail captured from video');
        } else {
          toast.dismiss('capture');
          toast.error('Failed to capture thumbnail');
        }
      } catch (error) {
        toast.dismiss('capture');
        console.error('Failed to capture frame:', error);
        toast.error('Failed to capture thumbnail');
      }
    }, 'image/jpeg', 0.9);
  };

  // Format time to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Seek video
  const seekVideo = (time) => {
    if (videoPreviewRef.current) {
      videoPreviewRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const hasVideo = shot.video_url && shot.media_type === 'video';
  
  return (
    <div className="fixed right-0 top-0 bottom-0 w-[420px] bg-black border-l border-white/20 z-40 flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-white/20">
        <h3 className="text-white font-semibold">Shot Details</h3>
        <button onClick={onClose} className="text-white/60 hover:text-white">
          <X size={20} />
        </button>
      </div>
      
      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Media Preview */}
        <div>
          <label className="block text-white/60 text-sm mb-2">
            {hasVideo ? 'Video Preview' : 'Thumbnail'}
          </label>
          
          {hasVideo ? (
            <div className="space-y-3">
              {/* Video Player */}
              <div className="aspect-video bg-black border border-white/20 rounded-lg overflow-hidden relative">
                <video 
                  ref={videoPreviewRef}
                  src={shot.video_url}
                  className="w-full h-full object-contain"
                  onLoadedMetadata={handleVideoLoaded}
                  onTimeUpdate={handleTimeUpdate}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  muted
                />
                
                {/* Play/Pause Overlay */}
                <button
                  onClick={togglePlay}
                  className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity"
                >
                  <div className="w-14 h-14 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                    {isPlaying ? (
                      <Pause size={24} className="text-white" fill="white" />
                    ) : (
                      <Play size={24} className="text-white ml-1" fill="white" />
                    )}
                  </div>
                </button>
              </div>
              
              {/* Video Controls */}
              <div className="space-y-2">
                {/* Timeline */}
                <div className="relative">
                  <input
                    type="range"
                    min="0"
                    max={videoDuration || 100}
                    value={currentTime}
                    onChange={(e) => seekVideo(parseFloat(e.target.value))}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-white"
                  />
                  {/* Trim markers */}
                  {shot.trim_start > 0 && (
                    <div 
                      className="absolute top-0 h-2 bg-green-500/30 rounded-l"
                      style={{ left: 0, width: `${(shot.trim_start / videoDuration) * 100}%` }}
                    />
                  )}
                  {shot.trim_end && (
                    <div 
                      className="absolute top-0 h-2 bg-red-500/30 rounded-r"
                      style={{ left: `${(shot.trim_end / videoDuration) * 100}%`, right: 0 }}
                    />
                  )}
                </div>
                
                {/* Time display */}
                <div className="flex items-center justify-between text-xs text-white/60">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(videoDuration)}</span>
                </div>
                
                {/* Trim Controls */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setShowTrimEditor(!showTrimEditor);
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      showTrimEditor ? 'bg-white text-black' : 'border border-white/20 text-white hover:bg-white/10'
                    }`}
                  >
                    <Scissors size={14} />
                    Trim Video
                  </button>
                </div>
                
                {/* Full Trim Editor (same as Video Vault) */}
                {showTrimEditor && (
                  <div className="mt-3 bg-black/30 border border-white/20 rounded-lg p-3">
                    <VideoTrimEditor
                      videoUrl={shot.video_url}
                      trimStart={shot.trim_start || 0}
                      trimEnd={shot.trim_end}
                      onTrimChange={(trimData) => {
                        // trimData now contains: video_url (new trimmed video), trim_start, trim_end, duration_start, duration_end
                        onUpdate(trimData);
                      }}
                      onCaptureThumbnail={(url) => {
                        onUpdate({ thumbnail_url: url });
                      }}
                      userId={shot.user_id || currentUserId}
                    />
                  </div>
                )}
                
                {/* Capture Frame Button */}
                <button
                  onClick={captureFrame}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-white/20 rounded-lg text-white hover:bg-white/10 text-sm"
                  title="Use current frame as thumbnail"
                >
                  <ImageIcon size={14} />
                  Capture Current Frame as Thumbnail
                </button>
              </div>
              
              {/* Hidden canvas for frame capture */}
              <canvas ref={canvasRef} className="hidden" />
              
              {/* Upload buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => videoInputRef.current?.click()}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-white/20 rounded-lg text-white/70 hover:bg-white/10 text-sm"
                >
                  <FileVideo size={14} />
                  Replace Video
                </button>
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-white/20 rounded-lg text-white/70 hover:bg-white/10 text-sm"
                >
                  <FileImage size={14} />
                  Use Image Instead
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Image Preview */}
              <div 
                className="aspect-video bg-black/50 border border-white/20 rounded-lg overflow-hidden cursor-pointer hover:border-white/40 transition-colors"
                onClick={() => imageInputRef.current?.click()}
              >
                {shot.thumbnail_url ? (
                  <img src={shot.thumbnail_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                    <Upload size={24} className="text-white/30" />
                    <span className="text-white/30 text-sm">Click to upload</span>
                  </div>
                )}
              </div>
              
              {/* Upload buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-white/20 rounded-lg text-white/70 hover:bg-white/10 text-sm"
                >
                  <FileImage size={14} />
                  Upload Image
                </button>
                <button
                  onClick={() => videoInputRef.current?.click()}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-blue-500/30 rounded-lg text-blue-400 hover:bg-blue-500/10 text-sm"
                >
                  <FileVideo size={14} />
                  Upload Video
                </button>
              </div>
              
              {/* Video URL Input */}
              <div className="mt-3">
                <label className="block text-white/50 text-xs mb-1">Or paste video URL</label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    placeholder="https://example.com/video.mp4"
                    className="flex-1 bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:border-white/40 focus:outline-none placeholder:text-white/30"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const url = e.target.value.trim();
                        if (url) {
                          onUpdate({ video_url: url, media_type: 'video' });
                          e.target.value = '';
                          toast.success('Video URL added');
                        }
                      }
                    }}
                  />
                  <button
                    onClick={(e) => {
                      const input = e.target.closest('.flex').querySelector('input');
                      const url = input?.value?.trim();
                      if (url) {
                        onUpdate({ video_url: url, media_type: 'video' });
                        input.value = '';
                        toast.success('Video URL added');
                      }
                    }}
                    className="px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 text-sm"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
          
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={onUploadImage}
            className="hidden"
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            onChange={onUploadVideo}
            className="hidden"
          />
        </div>
        
        {/* Scene & Shot Number */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-white/60 text-sm mb-2">Scene #</label>
            <input
              type="number"
              value={shot.scene_number || 1}
              onChange={(e) => onUpdate({ scene_number: parseInt(e.target.value) || 1 })}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-white/40 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-2">Shot #</label>
            <input
              type="number"
              value={shot.shot_number || 1}
              onChange={(e) => onUpdate({ shot_number: parseInt(e.target.value) || 1 })}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-white/40 focus:outline-none"
            />
          </div>
        </div>
        
        {/* Duration */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-white/60 text-sm mb-2">Start Time</label>
            <input
              type="text"
              value={shot.duration_start || '00:00'}
              onChange={(e) => onUpdate({ duration_start: e.target.value })}
              placeholder="00:00"
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-white/40 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-white/60 text-sm mb-2">End Time</label>
            <input
              type="text"
              value={shot.duration_end || '00:08'}
              onChange={(e) => onUpdate({ duration_end: e.target.value })}
              placeholder="00:08"
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-white/40 focus:outline-none"
            />
          </div>
        </div>
        
        {/* Shot Type */}
        <div>
          <label className="block text-white/60 text-sm mb-2">Shot Type</label>
          <select
            value={shot.shot_type || 'Medium Shot'}
            onChange={(e) => onUpdate({ shot_type: e.target.value })}
            className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-white/40 focus:outline-none"
          >
            {SHOT_TYPES.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        
        {/* Character */}
        <div>
          <label className="block text-white/60 text-sm mb-2">Character</label>
          <input
            type="text"
            value={shot.character_name || ''}
            onChange={(e) => onUpdate({ character_name: e.target.value })}
            placeholder="Character name"
            className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-white/40 focus:outline-none"
          />
        </div>
        
        {/* Dialogue */}
        <div>
          <label className="block text-white/60 text-sm mb-2">Dialogue</label>
          <textarea
            value={shot.dialogue || ''}
            onChange={(e) => onUpdate({ dialogue: e.target.value })}
            placeholder="What the character says..."
            rows={3}
            className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-white/40 focus:outline-none resize-none"
          />
        </div>
        
        {/* Camera Movement */}
        <div>
          <label className="block text-white/60 text-sm mb-2">Camera Movement / Description</label>
          <textarea
            value={shot.camera_movement || ''}
            onChange={(e) => onUpdate({ camera_movement: e.target.value })}
            placeholder="Describe the camera movement and shot..."
            rows={3}
            className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-white/40 focus:outline-none resize-none"
          />
        </div>
        
        {/* Lens */}
        <div>
          <label className="block text-white/60 text-sm mb-2">Lens</label>
          <input
            type="text"
            value={shot.lens || ''}
            onChange={(e) => onUpdate({ lens: e.target.value })}
            placeholder="e.g., 50mm, 24-70mm"
            className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-white/40 focus:outline-none"
          />
        </div>
        
        {/* Lighting Notes */}
        <div>
          <label className="block text-white/60 text-sm mb-2">Lighting Notes</label>
          <textarea
            value={shot.lighting_notes || ''}
            onChange={(e) => onUpdate({ lighting_notes: e.target.value })}
            placeholder="Describe the lighting setup..."
            rows={2}
            className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-white/40 focus:outline-none resize-none"
          />
        </div>
        
        {/* Audio Notes */}
        <div>
          <label className="block text-white/60 text-sm mb-2">Audio Notes</label>
          <textarea
            value={shot.audio_notes || ''}
            onChange={(e) => onUpdate({ audio_notes: e.target.value })}
            placeholder="Sound effects, music cues, etc."
            rows={2}
            className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-white/40 focus:outline-none resize-none"
          />
        </div>
      </div>
      
      {/* Save Changes Button - Fixed at bottom */}
      <div className="p-4 border-t border-white/20 bg-black">
        <button
          onClick={() => {
            toast.success('Changes saved');
            onClose();
          }}
          className="w-full py-3 bg-white text-black rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
          data-testid="save-shot-changes-btn"
        >
          <Save size={18} />
          Save Changes
        </button>
      </div>
    </div>
  );
};


// ============ MAIN MOODBOARD PAGE ============
const Moodboard = () => {
  const userId = useUserId();
  const { isDemoMode, triggerViewOnlyWarning } = useDemoMode();
  
  const [activeTab, setActiveTab] = useState('moodboard');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [elements, setElements] = useState([]);
  const [shots, setShots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParentId, setNewFolderParentId] = useState(null); // Parent folder for nested subfolders
  const [newFolderProjectId, setNewFolderProjectId] = useState(null); // Project this folder belongs to
  const [expandedFolders, setExpandedFolders] = useState(new Set()); // Track which folders/projects are expanded
  const [vaultSelections, setVaultSelections] = useState([]); // Items selected from vault to add to storyboard

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      if (!userId) return;
      
      try {
        const [projectsRes, foldersRes] = await Promise.all([
          fetch(`${API_URL}/api/moodboard/projects?user_id=${encodeURIComponent(userId)}&type=${activeTab}`),
          fetch(`${API_URL}/api/moodboard/folders?user_id=${encodeURIComponent(userId)}`)
        ]);
        
        if (projectsRes.ok) {
          const data = await projectsRes.json();
          setProjects(data);
        }
        
        if (foldersRes.ok) {
          const data = await foldersRes.json();
          setFolders(data);
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, [userId, activeTab]);

  // Fetch project elements/shots when project is selected
  useEffect(() => {
    const fetchProjectData = async () => {
      if (!selectedProject) return;
      
      try {
        if (activeTab === 'moodboard') {
          const response = await fetch(`${API_URL}/api/moodboard/elements?project_id=${selectedProject.id}`);
          if (response.ok) {
            const data = await response.json();
            setElements(data);
          }
        } else {
          const response = await fetch(`${API_URL}/api/moodboard/shots?project_id=${selectedProject.id}`);
          if (response.ok) {
            const data = await response.json();
            setShots(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch project data:', error);
      }
    };
    
    fetchProjectData();
  }, [selectedProject, activeTab]);

  // Create new project
  const createProject = async () => {
    if (!newProjectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }
    
    if (isDemoMode) {
      triggerViewOnlyWarning();
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/moodboard/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newProjectName,
          user_id: userId,
          type: activeTab,
          folder_id: selectedFolder
        })
      });
      
      if (response.ok) {
        const project = await response.json();
        setProjects(prev => [...prev, project]);
        setSelectedProject(project);
        setNewProjectName('');
        setShowNewProjectModal(false);
        toast.success('Project created');
      }
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project');
    }
  };

  // Delete project
  const deleteProject = async (projectId) => {
    if (isDemoMode) {
      triggerViewOnlyWarning();
      return;
    }
    
    try {
      await fetch(`${API_URL}/api/moodboard/projects/${projectId}`, {
        method: 'DELETE'
      });
      
      setProjects(prev => prev.filter(p => p.id !== projectId));
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
        setElements([]);
        setShots([]);
      }
      toast.success('Project deleted');
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  };

  // Create folder (subfolder for a project)
  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    
    if (isDemoMode) {
      triggerViewOnlyWarning();
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/moodboard/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newFolderName,
          user_id: userId,
          project_id: newFolderProjectId || null,
          parent_id: newFolderParentId || null
        })
      });
      
      if (response.ok) {
        const folder = await response.json();
        setFolders(prev => [...prev, folder]);
        setNewFolderName('');
        setNewFolderParentId(null);
        setNewFolderProjectId(null);
        setShowFolderModal(false);
        
        // Auto-expand parent folder or project if creating a subfolder
        if (folder.parent_id) {
          setExpandedFolders(prev => new Set([...prev, folder.parent_id]));
        } else if (folder.project_id) {
          setExpandedFolders(prev => new Set([...prev, `project_${folder.project_id}`]));
        }
        toast.success('Folder created');
      }
    } catch (error) {
      console.error('Failed to create folder:', error);
    }
  };

  // Toggle folder expansion
  const toggleFolderExpand = (folderId, e) => {
    e.stopPropagation();
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  // Get subfolders for a parent
  const getSubfolders = (parentId) => {
    return folders.filter(f => f.parent_id === parentId);
  };

  // Get root folders (no parent)
  const rootFolders = folders.filter(f => !f.parent_id);

  // Open folder modal for creating subfolder within a folder
  const openNewSubfolderModal = (parentFolderId, e) => {
    e.stopPropagation();
    const parentFolder = folders.find(f => f.id === parentFolderId);
    setNewFolderParentId(parentFolderId);
    setNewFolderProjectId(parentFolder?.project_id || null); // Inherit project from parent
    setShowFolderModal(true);
  };

  // Delete folder (and all subfolders recursively on backend)
  const deleteFolder = async (folderId, e) => {
    e.stopPropagation();
    
    if (isDemoMode) {
      triggerViewOnlyWarning();
      return;
    }
    
    const folder = folders.find(f => f.id === folderId);
    const folderName = folder?.name || 'folder';
    
    if (!window.confirm(`Delete "${folderName}" and all its subfolders? Projects inside will be moved to root.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/moodboard/folders/${folderId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        // Remove the folder and all its subfolders from local state
        const getAllSubfolderIds = (parentId) => {
          const subIds = folders.filter(f => f.parent_id === parentId).map(f => f.id);
          return subIds.concat(subIds.flatMap(id => getAllSubfolderIds(id)));
        };
        
        const idsToRemove = [folderId, ...getAllSubfolderIds(folderId)];
        setFolders(prev => prev.filter(f => !idsToRemove.includes(f.id)));
        
        // If the deleted folder was selected, deselect it
        if (idsToRemove.includes(selectedFolder)) {
          setSelectedFolder(null);
        }
        
        toast.success(`Deleted "${folderName}"`);
      }
    } catch (error) {
      console.error('Failed to delete folder:', error);
      toast.error('Failed to delete folder');
    }
  };

  // Filter projects by folder
  const filteredProjects = selectedFolder 
    ? projects.filter(p => p.folder_id === selectedFolder)
    : projects.filter(p => !p.folder_id);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="moodboard-page">
      {/* Header */}
      <div className="border-b border-white/20 bg-black/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">Visual Planning</h1>
              <p className="text-white/60 text-sm mt-1">
                {selectedProject 
                  ? `Project: ${selectedProject.name}` 
                  : 'Create moodboards and storyboards for your projects'}
              </p>
            </div>
            
            {/* Tabs - Different based on whether a project is selected */}
            <div className="flex items-center gap-2 bg-white/5 rounded-xl p-1">
              {!selectedProject ? (
                /* No project selected - just show Projects tab as active */
                <button
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-white text-black"
                  data-testid="projects-tab"
                >
                  <div className="flex items-center gap-2">
                    <Folder size={16} />
                    All Projects
                  </div>
                </button>
              ) : (
                /* Project selected - show Back button + ALL project tabs (Moodboard, Storyboard, Vault, Details) */
                <>
                  <button
                    onClick={() => { setSelectedProject(null); setActiveTab('projects'); }}
                    className="px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-all"
                    data-testid="back-to-projects-btn"
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <ChevronRight size={14} className="rotate-180 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Projects</span>
                      <span className="sm:hidden">Back</span>
                    </div>
                  </button>
                  <div className="w-px h-5 sm:h-6 bg-white/20 mx-0.5 sm:mx-1"></div>
                  <button
                    onClick={() => setActiveTab('moodboard')}
                    className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                      activeTab === 'moodboard' 
                        ? 'bg-white text-black' 
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="moodboard-tab"
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Layout size={14} className="sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Moodboard</span>
                      <span className="sm:hidden">Mood</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('storyboard')}
                    className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                      activeTab === 'storyboard' 
                        ? 'bg-white text-black' 
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="storyboard-tab"
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Clapperboard size={14} className="sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Storyboard</span>
                      <span className="sm:hidden">Story</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('vault')}
                    className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                      activeTab === 'vault' 
                        ? 'bg-white text-black' 
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="vault-tab"
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Archive size={14} className="sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Video Vault</span>
                      <span className="sm:hidden">Vault</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                      activeTab === 'details'
                        ? 'bg-white text-black'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="details-tab"
                  >
                    <div className="flex items-center gap-1 sm:gap-2">
                      <FileText size={14} className="sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Details</span>
                      <span className="sm:hidden">Info</span>
                    </div>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row h-[calc(100vh-80px)]">
        {/* Sidebar - Folders within selected project (only for moodboard/storyboard tabs) */}
        {selectedProject && (activeTab === 'moodboard' || activeTab === 'storyboard') && (
        <div className="w-full md:w-56 lg:w-64 border-b md:border-b-0 md:border-r border-white/20 flex flex-col bg-black/50 max-h-48 md:max-h-none overflow-hidden">
          <div className="p-3 md:p-4 border-b border-white/10">
            <div className="flex items-center justify-between mb-2 md:mb-3">
              <h2 className="text-white/80 text-xs md:text-sm font-semibold uppercase tracking-wider">Folders</h2>
              <button
                onClick={() => {
                  setNewFolderProjectId(selectedProject.id);
                  setShowFolderModal(true);
                }}
                className="p-1 md:p-1.5 hover:bg-white/10 rounded text-white/60 hover:text-white"
                title="New Folder"
              >
                <FolderPlus size={14} className="md:w-4 md:h-4" />
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-auto p-2 flex md:flex-col flex-row gap-1 md:gap-0">
            {/* All Items link */}
            <button
              onClick={() => setSelectedFolder(null)}
              className={`flex-shrink-0 md:w-full flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-left transition-colors mb-0 md:mb-2 ${
                selectedFolder === null
                  ? 'bg-white/10 text-white' 
                  : 'text-white/70 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Grid size={14} className="md:w-4 md:h-4" />
              <span className="text-xs md:text-sm whitespace-nowrap">All {activeTab === 'moodboard' ? 'Elements' : 'Shots'}</span>
            </button>
            
            {/* Folders for current project */}
            {(() => {
              // Get folders for the selected project only
              const projectFolders = folders.filter(f => f.project_id === selectedProject?.id && !f.parent_id);
              
              // Recursive folder renderer
              const renderFolder = (folder, depth = 0) => {
                const subfolders = folders.filter(f => f.parent_id === folder.id);
                const hasSubs = subfolders.length > 0;
                const isFolderExpanded = expandedFolders.has(folder.id);
                const isFolderSelected = selectedFolder === folder.id;
                
                return (
                  <div key={folder.id}>
                    <div
                      className={`group flex items-center gap-1 px-2 py-1.5 rounded-lg cursor-pointer transition-colors mb-0.5 ${
                        isFolderSelected 
                          ? 'bg-white/10 text-white' 
                          : 'text-white/70 hover:bg-white/5 hover:text-white'
                      }`}
                      style={{ paddingLeft: `${12 + depth * 16}px` }}
                      onClick={() => {
                        if (isFolderSelected) {
                          setSelectedFolder(null);
                        } else {
                          setSelectedFolder(folder.id);
                        }
                      }}
                    >
                      <button
                        onClick={(e) => toggleFolderExpand(folder.id, e)}
                        className={`p-0.5 hover:bg-white/10 rounded transition-colors ${!hasSubs ? 'invisible' : ''}`}
                      >
                        {isFolderExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                      <Folder size={12} className="flex-shrink-0 text-blue-400" />
                      <span className="text-xs flex-1 truncate">{folder.name}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); openNewSubfolderModal(folder.id, e); }}
                        className="p-0.5 hover:bg-white/10 rounded opacity-0 group-hover:opacity-100"
                        title="Add subfolder"
                      >
                        <FolderPlus size={10} />
                      </button>
                      <button
                        onClick={(e) => deleteFolder(folder.id, e)}
                        className="p-0.5 hover:bg-red-500/20 text-white/40 hover:text-red-400 rounded opacity-0 group-hover:opacity-100"
                        title="Delete"
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                    {isFolderExpanded && hasSubs && (
                      <div>{subfolders.map(sf => renderFolder(sf, depth + 1))}</div>
                    )}
                  </div>
                );
              };
              
              if (projectFolders.length === 0) {
                return (
                  <div className="text-center py-4">
                    <p className="text-white/40 text-xs">No folders yet</p>
                  </div>
                );
              }
              
              return (
                <div className="space-y-0.5">
                  {projectFolders.map(folder => renderFolder(folder, 0))}
                </div>
              );
            })()}
          </div>
        </div>
        )}
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {!selectedProject ? (
            /* No project selected - show Projects Grid */
            <ProjectsGridView
              userId={userId}
              onOpenProject={(proj) => {
                setSelectedProject(proj);
                // Default to moodboard tab for all projects
                setActiveTab('moodboard');
              }}
            />
          ) : activeTab === 'details' ? (
            <ProjectDetailsView
              project={selectedProject}
              userId={userId}
              onUpdated={(updated) => {
                // Update selectedProject with the latest data
                setSelectedProject(updated);
                // Also update the projects array so it persists when navigating
                setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
              }}
            />
          ) : activeTab === 'vault' ? (
            <VideoVault 
              userId={userId}
              projectId={selectedProject?.id}
              onSelectForStoryboard={async (items, projectId, folderId) => {
                // When user selects items from vault with a target project/folder
                if (!items || items.length === 0) return;
                
                // Set vault selections with folder info
                setVaultSelections(items.map(item => ({
                  ...item,
                  targetFolderId: folderId
                })));
                
                // If a folder was selected, also set the selected folder
                if (folderId) {
                  setSelectedFolder(folderId);
                }
                
                // Switch to storyboard tab (stay in current project)
                setActiveTab('storyboard');
                toast.success(`${items.length} items ready to add to storyboard`);
              }}
            />
          ) : activeTab === 'moodboard' && selectedProject ? (
            <MoodboardCanvas 
              project={selectedProject}
              elements={elements}
              setElements={setElements}
            />
          ) : activeTab === 'storyboard' && selectedProject ? (
            <StoryboardView 
              project={selectedProject}
              shots={shots}
              setShots={setShots}
              vaultSelections={vaultSelections}
              clearVaultSelections={() => setVaultSelections([])}
              selectedFolder={selectedFolder}
              folders={folders}
              onFolderUpdate={(updatedFolder) => {
                // Update the folder in local state
                setFolders(prev => prev.map(f => f.id === updatedFolder.id ? updatedFolder : f));
              }}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-6 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center">
                  <Folder size={40} className="text-white/20" />
                </div>
                <h3 className="text-white/60 text-xl mb-2">Select a project to get started</h3>
                <p className="text-white/40 text-sm">
                  Click on a project from the grid to open it
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowNewProjectModal(false)}>
          <div 
            className="bg-black border border-white/30 rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-lg font-semibold">
                New {activeTab === 'moodboard' ? 'Moodboard' : 'Storyboard'}
              </h3>
              <button onClick={() => setShowNewProjectModal(false)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">Project Name</label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Enter project name..."
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-white/40 focus:outline-none"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && createProject()}
                />
              </div>
              
              <button
                onClick={createProject}
                className="w-full py-3 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Create Project
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* New Folder Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => { setShowFolderModal(false); setNewFolderParentId(null); setNewFolderProjectId(null); }}>
          <div 
            className="bg-black border border-white/30 rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-lg font-semibold">
                New {newFolderParentId ? 'Subfolder' : 'Folder'}
              </h3>
              <button onClick={() => { setShowFolderModal(false); setNewFolderParentId(null); setNewFolderProjectId(null); }} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Show project context */}
              {newFolderProjectId && !newFolderParentId && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <Film size={16} className="text-blue-400" />
                  <span className="text-white/70 text-sm">
                    Adding folder to: <span className="text-white font-medium">{projects.find(p => p.id === newFolderProjectId)?.name || 'Project'}</span>
                  </span>
                </div>
              )}
              
              {/* Parent folder indicator */}
              {newFolderParentId && (
                <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg border border-white/10">
                  <Folder size={16} className="text-blue-400" />
                  <span className="text-white/70 text-sm">
                    Inside: {folders.find(f => f.id === newFolderParentId)?.name || 'Unknown'}
                  </span>
                  <button 
                    onClick={() => setNewFolderParentId(null)}
                    className="ml-auto text-white/40 hover:text-white"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              
              <div>
                <label className="block text-white/60 text-sm mb-2">Folder Name</label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Enter folder name..."
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-white/40 focus:outline-none"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && createFolder()}
                />
              </div>
              
              <button
                onClick={createFolder}
                className="w-full py-3 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Create {newFolderParentId ? 'Subfolder' : 'Folder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Moodboard;

// ========== Projects Grid View — Lists every project of all types ==========
const ProjectsGridView = ({ userId, onOpenProject, onCreateProject }) => {
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  
  // Edit project state
  const [editingProject, setEditingProject] = useState(null);
  const [editProjectName, setEditProjectName] = useState('');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // Fetch all projects (no type filter needed anymore)
      const res = await fetch(`${API_URL}/api/moodboard/projects?user_id=${userId}`);
      if (res.ok) {
        const projects = await res.json();
        setAllProjects(Array.isArray(projects) ? projects : []);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const createProject = async () => {
    if (!newProjectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/moodboard/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newProjectName.trim(),
          user_id: userId,
          type: 'project' // Generic project type - has both moodboard and storyboard
        })
      });
      
      if (response.ok) {
        const project = await response.json();
        toast.success('Project created!');
        setShowNewModal(false);
        setNewProjectName('');
        // Open the new project directly
        onOpenProject(project);
      } else {
        toast.error('Failed to create project');
      }
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project');
    }
  };

  // Delete project
  const deleteProject = async (projectId, e) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this project? This will delete all moodboard elements, storyboard shots, and vault items within it.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/moodboard/projects/${projectId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setAllProjects(prev => prev.filter(p => p.id !== projectId));
        toast.success('Project deleted');
      } else {
        toast.error('Failed to delete project');
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project');
    }
  };

  // Edit project name
  const updateProjectName = async () => {
    if (!editProjectName.trim() || !editingProject) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/moodboard/projects/${editingProject.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editProjectName.trim() })
      });
      
      if (response.ok) {
        const updated = await response.json();
        setAllProjects(prev => prev.map(p => p.id === editingProject.id ? { ...p, name: updated.name } : p));
        toast.success('Project renamed');
        setEditingProject(null);
        setEditProjectName('');
      } else {
        toast.error('Failed to rename project');
      }
    } catch (error) {
      console.error('Failed to rename project:', error);
      toast.error('Failed to rename project');
    }
  };

  // Open edit modal
  const openEditModal = (project, e) => {
    e.stopPropagation();
    setEditingProject(project);
    setEditProjectName(project.name);
  };

  const filtered = allProjects.filter(p => {
    if (query && !(p.name || '').toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="w-full h-full overflow-auto p-3 sm:p-4 md:p-8 bg-black">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white">All Projects</h2>
            <p className="text-xs sm:text-sm text-white/60 mt-1">{filtered.length} project{filtered.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-initial">
              <Search size={14} className="sm:w-4 sm:h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search projects…"
                className="w-full sm:w-auto pl-8 sm:pl-9 pr-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-xs sm:text-sm focus:outline-none focus:border-white/40"
                data-testid="projects-search"
              />
            </div>
            <button
              onClick={() => setShowNewModal(true)}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors text-xs sm:text-sm font-medium whitespace-nowrap"
              data-testid="new-project-btn"
            >
              <Plus size={14} className="sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">New Project</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-white/50">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-10 sm:py-20 text-center border border-dashed border-white/20 rounded-2xl px-4">
            <Folder size={32} className="sm:w-10 sm:h-10 mx-auto text-white/30 mb-2 sm:mb-3" />
            <p className="text-white/60 text-sm sm:text-base">No projects yet.</p>
            <p className="text-white/40 text-xs sm:text-sm mt-1">Click "New Project" to create your first project.</p>
            <button
              onClick={() => setShowNewModal(true)}
              className="mt-3 sm:mt-4 flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors text-xs sm:text-sm font-medium mx-auto"
            >
              <Plus size={14} className="sm:w-4 sm:h-4" />
              Create Your First Project
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {filtered.map(p => (
              <div
                key={p.id}
                className="group relative text-left bg-white/[0.04] border border-white/10 hover:border-white/30 rounded-xl p-3 sm:p-4 transition-all hover:bg-white/[0.08] cursor-pointer"
                onClick={() => onOpenProject(p)}
                data-testid={`project-card-${p.id}`}
              >
                {/* Action buttons - top right */}
                <div className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2 flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10">
                  <button
                    onClick={(e) => openEditModal(p, e)}
                    className="p-1 sm:p-1.5 bg-black/60 hover:bg-white/20 rounded-lg text-white/70 hover:text-white transition-colors"
                    title="Rename project"
                    data-testid={`edit-project-${p.id}`}
                  >
                    <Edit3 size={12} className="sm:w-3.5 sm:h-3.5" />
                  </button>
                  <button
                    onClick={(e) => deleteProject(p.id, e)}
                    className="p-1 sm:p-1.5 bg-black/60 hover:bg-red-500/30 rounded-lg text-white/70 hover:text-red-400 transition-colors"
                    title="Delete project"
                    data-testid={`delete-project-${p.id}`}
                  >
                    <Trash2 size={12} className="sm:w-3.5 sm:h-3.5" />
                  </button>
                </div>
                
                <div
                  className="aspect-video rounded-lg mb-2 sm:mb-3 flex items-center justify-center border border-white/10"
                  style={{ background: p.color || '#1e293b' }}
                >
                  <Folder size={28} className="sm:w-9 sm:h-9 text-white/80" />
                </div>
                <div className="flex items-start justify-between gap-1 sm:gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-semibold truncate text-sm sm:text-base">{p.name}</h3>
                    <p className="text-white/40 text-[10px] sm:text-xs mt-0.5">Project</p>
                  </div>
                  <ChevronRight size={14} className="sm:w-4 sm:h-4 text-white/30 group-hover:text-white/70 mt-1 flex-shrink-0" />
                </div>
                {p.description && (
                  <p className="text-white/50 text-[10px] sm:text-xs mt-1 sm:mt-2 line-clamp-2">{p.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* New Project Modal - Simple name input only */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowNewModal(false)}>
          <div 
            className="bg-black border border-white/30 rounded-2xl p-4 sm:p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-lg font-semibold">New Project</h3>
              <button onClick={() => setShowNewModal(false)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">Project Name</label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Enter project name..."
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-white/40 focus:outline-none"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && createProject()}
                  data-testid="new-project-name-input"
                />
              </div>
              
              <p className="text-white/40 text-xs">
                Each project includes a Moodboard, Storyboard, Video Vault, and Details page.
              </p>
              
              <button
                onClick={createProject}
                className="w-full py-3 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors"
                data-testid="create-project-submit"
              >
                Create Project
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Project Modal */}
      {editingProject && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setEditingProject(null)}>
          <div 
            className="bg-black border border-white/30 rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-lg font-semibold">Rename Project</h3>
              <button onClick={() => setEditingProject(null)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">Project Name</label>
                <input
                  type="text"
                  value={editProjectName}
                  onChange={(e) => setEditProjectName(e.target.value)}
                  placeholder="Enter project name..."
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-white/40 focus:outline-none"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && updateProjectName()}
                  data-testid="edit-project-name-input"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setEditingProject(null)}
                  className="flex-1 py-3 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={updateProjectName}
                  className="flex-1 py-3 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  data-testid="save-project-name"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== Call Sheet Field Component (moved outside to prevent re-mount on each keystroke) ==========
const CallSheetField = ({ label, value, onChange, placeholder, editing }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.15em] text-white font-semibold mb-1">{label}</div>
    {editing ? (
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-white/30 text-white font-medium text-sm py-1 focus:outline-none focus:border-white placeholder:text-white/30"
        style={{ color: '#ffffff' }}
      />
    ) : (
      <div className="text-white text-sm py-1 min-h-[28px] border-b border-transparent">{value || <span className="text-white/30">—</span>}</div>
    )}
  </div>
);

// ========== Project Details View — Multiple Call Sheets ==========
const ProjectDetailsView = ({ project, userId, onUpdated }) => {
  const [callSheets, setCallSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNewSheetModal, setShowNewSheetModal] = useState(false);
  const [newSheetName, setNewSheetName] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);

  // Load call sheets for this project
  useEffect(() => {
    const loadCallSheets = async () => {
      try {
        const res = await fetch(`${API_URL}/api/moodboard/callsheets?project_id=${project.id}`);
        if (res.ok) {
          const sheets = await res.json();
          setCallSheets(sheets);
          // Auto-select first sheet or create default if none exist
          if (sheets.length > 0) {
            setSelectedSheet(sheets[0]);
          }
        }
      } catch (e) {
        console.error('Failed to load call sheets:', e);
      }
      setLoading(false);
    };
    loadCallSheets();
  }, [project.id]);

  const [form, setForm] = useState({
    name: '',
    production: project.name || '',
    director: '',
    producer: '',
    dp: '',
    editor: '',
    shoot_date: '',
    call_time: '',
    location: '',
    location_address: '',
    weather: '',
    client: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    notes: '',
    schedule: [],
    cast: [],
    crew: [],
  });

  // Update form when selected sheet changes
  useEffect(() => {
    if (selectedSheet) {
      setForm({
        name: selectedSheet.name || '',
        production: selectedSheet.production || project.name || '',
        director: selectedSheet.director || '',
        producer: selectedSheet.producer || '',
        dp: selectedSheet.dp || '',
        editor: selectedSheet.editor || '',
        shoot_date: selectedSheet.shoot_date || '',
        call_time: selectedSheet.call_time || '',
        location: selectedSheet.location || '',
        location_address: selectedSheet.location_address || '',
        weather: selectedSheet.weather || '',
        client: selectedSheet.client || '',
        contact_name: selectedSheet.contact_name || '',
        contact_phone: selectedSheet.contact_phone || '',
        contact_email: selectedSheet.contact_email || '',
        notes: selectedSheet.notes || '',
        schedule: Array.isArray(selectedSheet.schedule) ? selectedSheet.schedule : [],
        cast: Array.isArray(selectedSheet.cast) ? selectedSheet.cast : [],
        crew: Array.isArray(selectedSheet.crew) ? selectedSheet.crew : [],
      });
    }
  }, [selectedSheet, project.name]);

  // Create new call sheet
  const createCallSheet = async () => {
    if (!newSheetName.trim()) {
      toast.error('Please enter a name for the call sheet');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/moodboard/callsheets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: project.id,
          user_id: userId,
          name: newSheetName.trim(),
          production: project.name
        })
      });
      
      if (res.ok) {
        const newSheet = await res.json();
        setCallSheets(prev => [...prev, newSheet]);
        setSelectedSheet(newSheet);
        setShowNewSheetModal(false);
        setNewSheetName('');
        toast.success('Call sheet created');
      } else {
        toast.error('Failed to create call sheet');
      }
    } catch (e) {
      toast.error('Failed to create call sheet');
    }
  };

  // Save current call sheet
  const save = async () => {
    if (!selectedSheet) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/moodboard/callsheets/${selectedSheet.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const updated = await res.json();
        setCallSheets(prev => prev.map(s => s.id === updated.id ? updated : s));
        setSelectedSheet(updated);
        toast.success('Call sheet saved');
        setEditing(false);
      } else {
        toast.error('Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  // Delete call sheet
  const deleteCallSheet = async (sheetId) => {
    if (!confirm('Delete this call sheet?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/moodboard/callsheets/${sheetId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setCallSheets(prev => prev.filter(s => s.id !== sheetId));
        if (selectedSheet?.id === sheetId) {
          setSelectedSheet(callSheets.find(s => s.id !== sheetId) || null);
        }
        toast.success('Call sheet deleted');
      }
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  // Generate share link
  const generateShareLink = async () => {
    if (!selectedSheet) return;
    
    try {
      const res = await fetch(`${API_URL}/api/moodboard/callsheets/${selectedSheet.id}/share`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        const baseUrl = window.location.origin;
        const link = `${baseUrl}/share/callsheet/${data.share_token}`;
        setShareLink(link);
        setShowShareModal(true);
      }
    } catch (e) {
      toast.error('Failed to generate share link');
    }
  };

  // Copy share link to clipboard
  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success('Link copied to clipboard');
  };

  const addRow = (key) => setForm(f => ({ ...f, [key]: [...(f[key] || []), { time: '', desc: '', who: '', role: '', name: '' }] }));
  const updateRow = (key, idx, field, value) => setForm(f => ({
    ...f,
    [key]: f[key].map((r, i) => i === idx ? { ...r, [field]: value } : r)
  }));
  const removeRow = (key, idx) => setForm(f => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-auto bg-black">
      <div className="max-w-5xl mx-auto p-8">
        {/* Call Sheets List Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">Call Sheets</h2>
            <p className="text-white/50 text-sm">{callSheets.length} call sheet{callSheets.length !== 1 ? 's' : ''} for this project</p>
          </div>
          <button
            onClick={() => setShowNewSheetModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            data-testid="new-callsheet-btn"
          >
            <Plus size={16} />
            New Call Sheet
          </button>
        </div>

        {/* Call Sheets Tabs */}
        {callSheets.length > 0 && (
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
            {callSheets.map(sheet => (
              <div key={sheet.id} className="flex items-center">
                <button
                  onClick={() => { setSelectedSheet(sheet); setEditing(false); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    selectedSheet?.id === sheet.id
                      ? 'bg-white text-black'
                      : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {sheet.name || 'Untitled'}
                </button>
                {selectedSheet?.id === sheet.id && (
                  <button
                    onClick={() => deleteCallSheet(sheet.id)}
                    className="ml-1 p-1 text-white/40 hover:text-red-400 hover:bg-red-500/20 rounded"
                    title="Delete call sheet"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {callSheets.length === 0 && (
          <div className="text-center py-16 border border-dashed border-white/20 rounded-xl">
            <FileText size={40} className="mx-auto text-white/30 mb-3" />
            <p className="text-white/60">No call sheets yet</p>
            <p className="text-white/40 text-sm mt-1">Create your first call sheet to get started</p>
            <button
              onClick={() => setShowNewSheetModal(true)}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium mx-auto"
            >
              <Plus size={16} />
              Create Call Sheet
            </button>
          </div>
        )}

        {/* Selected Call Sheet */}
        {selectedSheet && (
          <div className="border border-white/30 rounded-lg bg-black text-white" data-testid="project-details-callsheet">
            {/* Header */}
            <div className="border-b border-white/30 p-5 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 mb-1">Call Sheet</div>
                <h2 className="text-2xl font-bold uppercase tracking-wide">{form.production || 'Untitled Production'}</h2>
                <p className="text-white/50 text-sm mt-1">{form.name}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={generateShareLink}
                  className="px-3 py-1.5 border border-white/40 text-white text-xs rounded hover:bg-white/10 flex items-center gap-1.5"
                  title="Share call sheet"
                  data-testid="share-callsheet-btn"
                >
                  <ExternalLink size={12} />
                  Share
                </button>
                {editing ? (
                  <>
                    <button onClick={() => setEditing(false)}
                      className="px-3 py-1.5 border border-white/40 text-white text-xs rounded hover:bg-white/10"
                      data-testid="details-cancel-btn">Cancel</button>
                    <button onClick={save} disabled={saving}
                      className="px-4 py-1.5 bg-white text-black text-xs font-semibold rounded disabled:opacity-50"
                      data-testid="details-save-btn">{saving ? 'Saving…' : 'Save'}</button>
                  </>
                ) : (
                  <button onClick={() => setEditing(true)}
                    className="px-4 py-1.5 border border-white/40 text-white text-xs rounded hover:bg-white/10"
                    data-testid="details-edit-btn">
                    Edit Details
                  </button>
                )}
              </div>
            </div>

          {/* Top grid — Production meta */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 p-5 border-b border-white/30">
            <CallSheetField editing={editing} label="Shoot Date" value={form.shoot_date} onChange={v => setForm(f => ({ ...f, shoot_date: v }))} placeholder="Apr 25, 2026" />
            <CallSheetField editing={editing} label="Call Time" value={form.call_time} onChange={v => setForm(f => ({ ...f, call_time: v }))} placeholder="06:30 AM" />
            <CallSheetField editing={editing} label="Weather" value={form.weather} onChange={v => setForm(f => ({ ...f, weather: v }))} placeholder="72°F / Sunny" />
            <CallSheetField editing={editing} label="Client" value={form.client} onChange={v => setForm(f => ({ ...f, client: v }))} placeholder="GOTRAX" />
          </div>

          {/* Crew block */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 p-5 border-b border-white/30">
            <CallSheetField editing={editing} label="Director" value={form.director} onChange={v => setForm(f => ({ ...f, director: v }))} />
            <CallSheetField editing={editing} label="Producer" value={form.producer} onChange={v => setForm(f => ({ ...f, producer: v }))} />
            <CallSheetField editing={editing} label="DP" value={form.dp} onChange={v => setForm(f => ({ ...f, dp: v }))} />
            <CallSheetField editing={editing} label="Editor" value={form.editor} onChange={v => setForm(f => ({ ...f, editor: v }))} />
          </div>

          {/* Location */}
          <div className="p-5 border-b border-white/30">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white font-semibold mb-3">Location</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <CallSheetField editing={editing} label="Name" value={form.location} onChange={v => setForm(f => ({ ...f, location: v }))} placeholder="Sunset Studios Stage 3" />
              <CallSheetField editing={editing} label="Address" value={form.location_address} onChange={v => setForm(f => ({ ...f, location_address: v }))} placeholder="1234 Vine St, Los Angeles, CA" />
            </div>
          </div>

          {/* Contact */}
          <div className="p-5 border-b border-white/30">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white font-semibold mb-3">Primary Contact</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <CallSheetField editing={editing} label="Name" value={form.contact_name} onChange={v => setForm(f => ({ ...f, contact_name: v }))} />
              <CallSheetField editing={editing} label="Phone" value={form.contact_phone} onChange={v => setForm(f => ({ ...f, contact_phone: v }))} />
              <CallSheetField editing={editing} label="Email" value={form.contact_email} onChange={v => setForm(f => ({ ...f, contact_email: v }))} />
            </div>
          </div>

          {/* Schedule */}
          <div className="p-5 border-b border-white/30">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white font-semibold">Schedule</div>
              {editing && (
                <button onClick={() => addRow('schedule')} className="text-xs text-white/70 hover:text-white flex items-center gap-1">
                  <Plus size={12} /> Add row
                </button>
              )}
            </div>
            {form.schedule.length === 0 ? (
              <div className="text-white/40 text-sm">No schedule items.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-white font-semibold border-b border-white/20">
                    <th className="py-2 w-32">Time</th>
                    <th className="py-2">Activity</th>
                    <th className="py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.schedule.map((row, idx) => (
                    <tr key={idx} className="border-b border-white/10">
                      <td className="py-2 pr-3">
                        {editing ? (
                          <input value={row.time || ''} onChange={e => updateRow('schedule', idx, 'time', e.target.value)}
                            placeholder="06:30 AM" className="w-full bg-transparent text-white text-sm focus:outline-none" />
                        ) : <span className="text-white">{row.time || '—'}</span>}
                      </td>
                      <td className="py-2 pr-3">
                        {editing ? (
                          <input value={row.desc || ''} onChange={e => updateRow('schedule', idx, 'desc', e.target.value)}
                            placeholder="Crew call / breakfast" className="w-full bg-transparent text-white text-sm focus:outline-none" />
                        ) : <span className="text-white">{row.desc || '—'}</span>}
                      </td>
                      <td className="py-2 text-right">
                        {editing && (
                          <button onClick={() => removeRow('schedule', idx)} className="text-white/40 hover:text-red-400">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Cast + Crew columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/30 border-b border-white/30">
            {['cast', 'crew'].map(key => (
              <div key={key} className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white font-semibold">{key}</div>
                  {editing && (
                    <button onClick={() => addRow(key)} className="text-xs text-white/70 hover:text-white flex items-center gap-1">
                      <Plus size={12} /> Add
                    </button>
                  )}
                </div>
                {form[key].length === 0 ? (
                  <div className="text-white/40 text-sm">No {key} listed.</div>
                ) : (
                  <div className="space-y-2">
                    {form[key].map((row, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        {editing ? (
                          <>
                            <input value={row.role || ''} onChange={e => updateRow(key, idx, 'role', e.target.value)}
                              placeholder="Role" className="w-28 bg-transparent border-b border-white/20 text-white text-sm focus:outline-none" />
                            <input value={row.name || ''} onChange={e => updateRow(key, idx, 'name', e.target.value)}
                              placeholder="Name" className="flex-1 bg-transparent border-b border-white/20 text-white text-sm focus:outline-none" />
                            <button onClick={() => removeRow(key, idx)} className="text-white/40 hover:text-red-400">
                              <Trash2 size={14} />
                            </button>
                          </>
                        ) : (
                          <div className="flex-1 flex items-center justify-between">
                            <span className="text-white/50 text-xs uppercase tracking-wide w-24">{row.role || '—'}</span>
                            <span className="text-white text-sm flex-1">{row.name || '—'}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Notes */}
          <div className="p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white font-semibold mb-2">Notes</div>
            {editing ? (
              <textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={4}
                className="w-full bg-transparent border border-white/30 rounded px-3 py-2 text-white font-medium text-sm focus:outline-none focus:border-white/70 resize-none"
                placeholder="Production notes, special instructions…"
                style={{ color: '#ffffff' }}
              />
            ) : (
              <div className="text-white/80 text-sm whitespace-pre-wrap min-h-[32px]">{form.notes || <span className="text-white/30">—</span>}</div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* New Call Sheet Modal */}
      {showNewSheetModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowNewSheetModal(false)}>
          <div 
            className="bg-black border border-white/30 rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-lg font-semibold">New Call Sheet</h3>
              <button onClick={() => setShowNewSheetModal(false)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">Call Sheet Name</label>
                <input
                  type="text"
                  value={newSheetName}
                  onChange={(e) => setNewSheetName(e.target.value)}
                  placeholder="e.g., Day 1 - Outdoor Shoot"
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white font-medium focus:border-white/40 focus:outline-none"
                  style={{ color: '#ffffff' }}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && createCallSheet()}
                  data-testid="new-callsheet-name-input"
                />
              </div>
              
              <button
                onClick={createCallSheet}
                className="w-full py-3 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors"
                data-testid="create-callsheet-submit"
              >
                Create Call Sheet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Link Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowShareModal(false)}>
          <div 
            className="bg-black border border-white/30 rounded-2xl p-6 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-lg font-semibold">Share Call Sheet</h3>
              <button onClick={() => setShowShareModal(false)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-white/60 text-sm">Anyone with this link can view the call sheet (no login required):</p>
              
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="flex-1 bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white text-sm"
                />
                <button
                  onClick={copyShareLink}
                  className="px-4 py-3 bg-white text-black rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
                >
                  <Copy size={16} />
                  Copy
                </button>
              </div>
              
              <p className="text-white/40 text-xs">This link will remain active until you regenerate it.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
